import { useEffect, useMemo, useRef, useState, lazy, Suspense, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, HttpError, type User, type Vendor, type Model, type Device, type Pop, type PopLink, type HealthOverview } from './api';
import { DEVICE_TABS, matchDeviceTab, type DeviceTab } from './lib/deviceTabs';
import { PopSubmap } from './PopSubmap';
import { LinkLivePanel } from './components/LinkLivePanel';
import { BgpBulkPage } from './components/BgpBulkPage';
import { LldpPage } from './components/LldpPage';
import { MplsPage } from './components/MplsPage';
import { FleetLayer } from './components/FleetLayer';
import { EventsPage } from './components/EventsPage';
import { NocAgentePage } from './components/NocAgentePage';
import { AtendimentoAgentePage } from './components/AtendimentoAgentePage';
import { MapSidePanel } from './components/MapSidePanel';
import { MapaOsm } from './components/MapaOsm';
import { AccessesSection } from './components/AccessesSection';
import { OpticalChart } from './components/OpticalChart';

const BackupsPage = lazy(() => import('./components/BackupsPage').then((m) => ({ default: m.BackupsPage })));
const Topo3DPage = lazy(() => import('./components/Topo3DPage').then((m) => ({ default: m.Topo3DPage })));
const CatalogPage = lazy(() => import('./components/CatalogPage').then((m) => ({ default: m.CatalogPage })));
const OnusOverviewPage = lazy(() => import('./components/OnusOverviewPage').then((m) => ({ default: m.OnusOverviewPage })));
const OnuSignalPage = lazy(() => import('./components/OnuSignalPage').then((m) => ({ default: m.OnuSignalPage })));
const OnuFlappingPage = lazy(() => import('./components/OnuFlappingPage').then((m) => ({ default: m.OnuFlappingPage })));
const OnuSanitationPage = lazy(() => import('./components/OnuSanitationPage').then((m) => ({ default: m.OnuSanitationPage })));
const HealthPage = lazy(() => import('./components/HealthPage').then((m) => ({ default: m.HealthPage })));
const FirmwarePage = lazy(() => import('./components/FirmwarePage').then((m) => ({ default: m.FirmwarePage })));
const DevicePage = lazy(() => import('./components/DevicePage').then((m) => ({ default: m.DevicePage })));
const CtoWatchPage = lazy(() => import('./components/CtoWatchPage').then((m) => ({ default: m.CtoWatchPage })));
const ProvidersPage = lazy(() => import('./components/ProvidersPage').then((m) => ({ default: m.ProvidersPage })));
const UsersPage = lazy(() => import('./components/UsersPage').then((m) => ({ default: m.UsersPage })));
const GroupsPage = lazy(() => import('./components/GroupsPage').then((m) => ({ default: m.GroupsPage })));
import { APIProvider, Map as GMap, AdvancedMarker, InfoWindow, useMap, Pin, useMapsLibrary } from '@vis.gl/react-google-maps';

const GMAPS_KEY = (import.meta as any).env?.VITE_GOOGLE_MAPS_API_KEY ?? '';
// SSO Google: vazio = botão "Entrar com Google" não renderiza e a tela de login
// fica idêntica à de antes. O Client ID é público (identifica o app, não autoriza
// nada); quem valida o token é o backend, contra o mesmo valor em GOOGLE_OAUTH_CLIENT_ID.
const GOOGLE_CLIENT_ID = (import.meta as any).env?.VITE_GOOGLE_OAUTH_CLIENT_ID ?? '';
const GMAPS_MAP_ID = 'DEMO_MAP_ID';
const SC_CENTER = { lat: -9.7, lng: -36.7 };  // centro aproximado AL

type Route = 'dashboard' | 'vendors' | 'models' | 'catalog' | 'devices' | 'device' | 'cto' | 'pops' | 'bgp' | 'optical' | 'map' | 'lldp' | 'mpls' | 'events' | 'health' | 'olts' | 'onus' | 'onu-signal' | 'onu-flapping' | 'onu-sanitation' | 'backups' | 'firmware' | 'providers' | 'users' | 'groups' | 'workers' | 'agente-noc' | 'agente-atendimento' | 'topo3d';

export default function App() {
  const meQ = useQuery({ queryKey: ['me'], queryFn: api.me, retry: false, staleTime: 60_000 });
  if (meQ.isLoading) return <Splash msg="carregando..." />;
  if (meQ.isError && (meQ.error as HttpError)?.status === 401) return <Login />;
  if (meQ.isError) return <Splash msg={`erro: ${(meQ.error as Error).message}`} />;
  return <Shell user={meQ.data!.user} />;
}

function Splash({ msg }: { msg: string }) {
  return <div className="min-h-screen flex items-center justify-center text-slate-400">{msg}</div>;
}

/**
 * Botão oficial do Google (GIS). O script só é baixado por quem chega na tela
 * de login — não entra no bundle nem pesa nas outras rotas. Sem
 * VITE_GOOGLE_OAUTH_CLIENT_ID o componente não renderiza nada.
 */
function GoogleSignInButton({ onCredential, onScriptError, disabled }: {
  onCredential: (credential: string) => void;
  onScriptError: (msg: string) => void;
  disabled: boolean;
}) {
  const slot = useRef<HTMLDivElement>(null);
  // Refs pros callbacks: o GIS guarda a função passada no initialize() e não a
  // troca depois. Sem isso o callback fecharia sobre o estado do primeiro render.
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;
  const onScriptErrorRef = useRef(onScriptError);
  onScriptErrorRef.current = onScriptError;

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    const SRC = 'https://accounts.google.com/gsi/client';
    let cancelled = false;

    const init = () => {
      const gid = window.google?.accounts?.id;
      if (cancelled || !gid || !slot.current) return;
      gid.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (resp: any) => {
          const cred = resp?.credential;
          if (cred) onCredentialRef.current(cred);
          else onScriptErrorRef.current('o Google não devolveu credencial');
        },
        // Popup em vez de redirect: a SPA não tem rota de callback, e o cookie
        // de sessão é emitido pelo POST do próprio front.
        ux_mode: 'popup',
        cancel_on_tap_outside: true
      });
      gid.renderButton(slot.current, {
        theme: 'filled_black',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        width: 320,
        locale: 'pt-BR'
      });
    };

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SRC}"]`);
    if (existing) {
      // Script já veio de um mount anterior: pode estar carregado ou em voo.
      if (window.google?.accounts?.id) init();
      else existing.addEventListener('load', init);
      return () => { cancelled = true; existing.removeEventListener('load', init); };
    }

    const tag = document.createElement('script');
    tag.src = SRC;
    tag.async = true;
    tag.defer = true;
    tag.onload = init;
    tag.onerror = () => onScriptErrorRef.current('não foi possível carregar o Google (rede ou bloqueio no navegador)');
    document.head.appendChild(tag);
    return () => { cancelled = true; };
  }, []);

  if (!GOOGLE_CLIENT_ID) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 text-[11px] text-slate-500">
        <span className="h-px flex-1 bg-slate-800" />ou<span className="h-px flex-1 bg-slate-800" />
      </div>
      {/* O iframe do Google ignora pointer-events do pai; durante o POST o
          overlay evita clique duplo abrindo dois popups. */}
      <div className="relative flex justify-center">
        <div ref={slot} />
        {disabled && <div className="absolute inset-0 bg-slate-900/60 cursor-wait" />}
      </div>
      <p className="text-[11px] text-slate-500 text-center">
        Use a conta Google com o mesmo e-mail cadastrado no IXC.
      </p>
    </div>
  );
}

// Desafio do código guardado no navegador pelos 10 min em que vale: no iPhone/iPad o Safari
// descarta a aba quando a pessoa vai ao app de e-mail buscar o código, e a página recarrega.
type DesafioLogin = { desafio: string; email: string; validadeMin: number };
const DESAFIO_KEY = 'nms.login.desafio';
function lerDesafio(): DesafioLogin | null {
  try {
    const v = JSON.parse(localStorage.getItem(DESAFIO_KEY) ?? 'null');
    if (v && typeof v.desafio === 'string' && v.exp > Date.now()) return { desafio: v.desafio, email: v.email, validadeMin: v.validadeMin };
    localStorage.removeItem(DESAFIO_KEY);
  } catch {}
  return null;
}
function gravarDesafio(d: DesafioLogin | null) {
  try {
    if (d) localStorage.setItem(DESAFIO_KEY, JSON.stringify({ ...d, exp: Date.now() + d.validadeMin * 60_000 }));
    else localStorage.removeItem(DESAFIO_KEY);
  } catch {}
}

function Login() {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  // Segundo passo: a senha certa devolve `codigo` e o e-mail (mascarado) para onde ele foi.
  const [desafio, setDesafioState] = useState<DesafioLogin | null>(lerDesafio);
  const setDesafio = (d: DesafioLogin | null) => { gravarDesafio(d); setDesafioState(d); };
  const [codigo, setCodigo] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => api.login(email, password),
    onSuccess: (r) => {
      if (r.codigo) { setDesafio(r.codigo); setCodigo(''); setAviso(`Código enviado para ${r.codigo.email}.`); return; }
      qc.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (e: any) => setErr(e.message ?? 'falha no login')
  });
  const c = useMutation({
    mutationFn: () => api.loginCodigo(desafio!.desafio, codigo),
    onSuccess: () => { gravarDesafio(null); qc.invalidateQueries({ queryKey: ['me'] }); },
    onError: (e: any) => {
      setErr(e.message ?? 'código inválido');
      // Vencido ou 5 erros: o desafio morreu, volta para a senha.
      if (e?.detail?.error === 'codigo_expirado') { setDesafio(null); setAviso(null); setPassword(''); }
    }
  });
  const g = useMutation({
    mutationFn: (credential: string) => api.loginGoogle(credential),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
    onError: (e: any) => setErr(e.message ?? 'falha no login com Google')
  });
  if (desafio) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-slate-900 rounded-lg p-6 ring-1 ring-slate-800 space-y-4">
          <form onSubmit={(e) => { e.preventDefault(); setErr(null); c.mutate(); }} className="space-y-4">
            <h1 className="text-xl font-semibold">Código de acesso</h1>
            <p className="text-xs text-slate-400">
              Enviamos um código de 6 dígitos para <b className="text-slate-300">{desafio.email}</b>. Vale por {desafio.validadeMin} minutos.
              Depois de entrar, este navegador não pede código por 7 dias.
            </p>
            <input className="w-full bg-slate-950 ring-1 ring-slate-800 rounded px-3 py-2 text-lg tracking-[0.5em] text-center font-mono focus:outline-none focus:ring-emerald-600"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              required
              autoFocus />
            <button disabled={c.isPending || codigo.length !== 6} className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 rounded py-2 text-sm font-semibold">
              {c.isPending ? 'conferindo...' : 'entrar'}
            </button>
          </form>
          <div className="flex justify-between text-xs">
            <button className="text-slate-400 hover:text-slate-200" onClick={() => { setDesafio(null); setAviso(null); setErr(null); }}>voltar</button>
            {/* Depois de a página recarregar a senha não existe mais: reenviar = entrar de novo. */}
            {password
              ? <button className="text-emerald-400 hover:text-emerald-300 disabled:opacity-50" disabled={m.isPending}
                  onClick={() => { setErr(null); m.mutate(); }}>
                  {m.isPending ? 'enviando...' : 'reenviar código'}
                </button>
              : <button className="text-emerald-400 hover:text-emerald-300" onClick={() => { setDesafio(null); setAviso(null); setErr(null); }}>
                  pedir outro código
                </button>}
          </div>
          {aviso && !err && <div className="text-emerald-300 text-sm bg-emerald-950/40 border border-emerald-900/60 rounded px-3 py-2">{aviso}</div>}
          {err && <div className="text-rose-400 text-sm bg-rose-950/40 border border-rose-900/60 rounded px-3 py-2">{err}</div>}
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-slate-900 rounded-lg p-6 ring-1 ring-slate-800 space-y-4">
        <form onSubmit={(e) => { e.preventDefault(); setErr(null); m.mutate(); }}
          className="space-y-4">
          <h1 className="text-xl font-semibold">NMS Super Connect</h1>
          <p className="text-xs text-slate-400">Acesso restrito à equipe NOC / Engenharia (mesmas credenciais do IXC).</p>
          <div>
            <label className="block text-xs text-slate-400 mb-1">E-mail ou Usuário IXC</label>
            <input className="w-full bg-slate-950 ring-1 ring-slate-800 rounded px-3 py-2 text-sm focus:outline-none focus:ring-emerald-600"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ex: jorgeiwano ou nome@superconnect.com.br"
              required
              autoFocus />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Senha</label>
            <input type="password" className="w-full bg-slate-950 ring-1 ring-slate-800 rounded px-3 py-2 text-sm focus:outline-none focus:ring-emerald-600"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Sua senha do IXC"
              required />
          </div>
          <button disabled={m.isPending || g.isPending} className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 rounded py-2 text-sm font-semibold">
            {m.isPending ? 'autenticando...' : 'entrar'}
          </button>
        </form>
        <GoogleSignInButton
          disabled={g.isPending || m.isPending}
          onCredential={(credential) => { setErr(null); g.mutate(credential); }}
          onScriptError={(msg) => setErr(msg)} />
        {err && <div className="text-rose-400 text-sm bg-rose-950/40 border border-rose-900/60 rounded px-3 py-2">{err}</div>}
        {g.isPending && <div className="text-xs text-slate-400 text-center">validando conta Google...</div>}
      </div>
    </div>
  );
}

function Shell({ user }: { user: User }) {
  const qc = useQueryClient();
  const [route, setRoute] = useState<Route>(() => parseRoute());
  const [subRoute, setSubRoute] = useState<string | null>(() => parseSubRoute());
  useEffect(() => {
    const onHash = () => { setRoute(parseRoute()); setSubRoute(parseSubRoute()); };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const go = (r: Route) => { window.location.hash = '#' + r; };
  const logout = useMutation({ mutationFn: () => api.logout(), onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }) });
  const [monOpen, setMonOpen] = useState(false);
  const [topOpen, setTopOpen] = useState(false);
  const [opOpen, setOpOpen] = useState(false);
  const [dmOpen, setDmOpen] = useState(false);
  const [adOpen, setAdOpen] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [isFull, setIsFull] = useState(typeof document !== 'undefined' && !!document.fullscreenElement);
  useEffect(() => {
    const onFs = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);
  // O WebKit do iOS não implementa Fullscreen API em elementos, e a Apple obriga
  // todo browser do iPhone a usar WebKit (Chrome/Firefox iOS incluídos). Antes
  // este toggle chamava requestFullscreen direto: lá a propriedade é `undefined`,
  // então a chamada lançava TypeError SÍNCRONO — o `.catch()` nem chegava a ser
  // registrado, e o botão falhava calado. Testar antes de chamar.
  const fsSupported = typeof document !== 'undefined'
    && !!document.fullscreenEnabled
    && typeof document.documentElement.requestFullscreen === 'function';
  // Fallback pra onde não há Fullscreen API: nenhuma API do iOS esconde a barra
  // do navegador, então "tela cheia real" é impossível — o que dá pra devolver de
  // área útil é esconder o header do NMS.
  const [focusMode, setFocusMode] = useState(false);
  const toggleFull = () => {
    if (!fsSupported) { setFocusMode((v) => !v); return; }
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else document.documentElement.requestFullscreen().catch(() => {});
  };
  useEffect(() => {
    if (!focusMode) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setFocusMode(false); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [focusMode]);
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    if (typeof document === 'undefined') return 'light';
    return (document.documentElement.dataset.theme as 'dark' | 'light') || 'light';
  });
  const toggleTheme = () => setTheme((t) => {
    const next = t === 'dark' ? 'light' : 'dark';
    document.documentElement.dataset.theme = next;
    try { localStorage.setItem('nms.theme', next); } catch {}
    return next;
  });
  const isAdmin = user.role === 'ADMIN';

  // F10.12 — banner global de deploys ativos (multi-operador)
  const activeDeploysQ = useQuery({
    queryKey: ['firmware-deployments-active'],
    queryFn: api.firmwareDeploymentsActive,
    refetchInterval: 5000,
    staleTime: 2000
  });
  const activeDeploys = activeDeploysQ.data?.items ?? [];

  // Central de Saúde — badge global de pendências (pull, agrega sinais de saúde)
  const healthQ = useQuery({
    queryKey: ['health-overview'],
    queryFn: api.healthOverview,
    refetchInterval: 45_000,
    staleTime: 30_000
  });

  return (
    <div className="min-h-screen flex flex-col">
      {/* Modo foco esconde o header inteiro — inclusive o botão que o desliga.
          Este botão flutuante é a única saída (junto com Esc), então não remover. */}
      {focusMode && (
        <button onClick={() => setFocusMode(false)}
          title="Sair do modo foco (Esc)" aria-label="Sair do modo foco"
          className="fixed top-2 right-2 z-50 px-2.5 py-1.5 rounded bg-slate-900/90 hover:bg-slate-800 text-slate-200 text-sm shadow ring-1 ring-slate-700">
          ⤡
        </button>
      )}
      <header className={`border-b border-slate-800 bg-slate-900/60 backdrop-blur sticky top-0 z-20 ${focusMode ? 'hidden' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <button onClick={() => go('dashboard')} className="font-bold text-emerald-400 cursor-pointer">NMS</button>
          <span className="text-slate-500 text-sm">Super Connect</span>

          <nav className="flex items-center gap-1 ml-6 relative">
            {/* Monitoramento */}
            <div onMouseLeave={() => setMonOpen(false)} className="relative">
              <button onMouseEnter={() => setMonOpen(true)} onClick={() => setMonOpen((v) => !v)}
                className={navBtn(['dashboard', 'optical', 'agente-noc', 'agente-atendimento'].includes(route))}>
                Monitoramento ▾
              </button>
              {monOpen && (
                <div className="absolute top-full left-0 bg-slate-900 ring-1 ring-slate-800 rounded shadow-lg w-52 py-1 mt-0">
                  <button onClick={() => { go('dashboard'); setMonOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">Dashboard</button>
                  <button onClick={() => { go('optical'); setMonOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">Óptica</button>
                  <button onClick={() => { go('agente-noc'); setMonOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">Agente NOC IA</button>
                  <button onClick={() => { go('agente-atendimento'); setMonOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">Agente Atendimento IA</button>
                </div>
              )}
            </div>

            {/* Topologia */}
            <div onMouseLeave={() => setTopOpen(false)} className="relative">
              <button onMouseEnter={() => setTopOpen(true)} onClick={() => setTopOpen((v) => !v)}
                className={navBtn(['map', 'lldp', 'topo3d'].includes(route))}>
                Topologia ▾
              </button>
              {topOpen && (
                <div className="absolute top-full left-0 bg-slate-900 ring-1 ring-slate-800 rounded shadow-lg w-44 py-1 mt-0">
                  <button onClick={() => { go('map'); setTopOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">Google Maps</button>
                  <button onClick={() => { go('lldp'); setTopOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">LLDP</button>
                  <button onClick={() => { go('topo3d'); setTopOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">Backbone 3D</button>
                </div>
              )}
            </div>


            <button onClick={() => go('events')} className={navBtn(route === 'events')}>Eventos</button>

            <button onClick={() => go('health')} className={navBtn(route === 'health')}>Saúde</button>

            {/* Operação (F12) — BGP, MPLS, GPON, fornecedores */}
            <div onMouseLeave={() => setOpOpen(false)} className="relative">
              <button onMouseEnter={() => setOpOpen(true)} onClick={() => setOpOpen((v) => !v)}
                className={navBtn(['providers', 'bgp', 'mpls', 'olts', 'onus', 'onu-signal', 'onu-flapping', 'onu-sanitation'].includes(route))}>
                Operação ▾
              </button>
              {opOpen && (
                <div className="absolute top-full left-0 bg-slate-900 ring-1 ring-slate-800 rounded shadow-lg w-48 py-1 mt-0">
                  <button onClick={() => { go('bgp'); setOpOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">BGP</button>
                  <button onClick={() => { go('mpls'); setOpOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">MPLS</button>
                  <div className="px-3 pt-2 pb-0.5 text-[10px] uppercase tracking-wide text-slate-500">GPON</div>
                  <button onClick={() => { go('olts'); setOpOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">OLTs</button>
                  <button onClick={() => { go('onus'); setOpOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">ONUs (frota)</button>
                  <button onClick={() => { go('onu-signal'); setOpOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">Saúde de sinal</button>
                  <button onClick={() => { go('onu-flapping'); setOpOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">Flapping / instabilidade</button>
                  <button onClick={() => { go('onu-sanitation'); setOpOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">Saneamento (offline antigo)</button>
                  <button disabled className="w-full text-left px-3 py-1.5 text-sm text-slate-600 cursor-not-allowed" title="F9.2 — pendente">Dying Gasp <span className="text-[10px]">(em breve)</span></button>
                  <div className="border-t border-slate-800 my-1"></div>
                  <button onClick={() => { go('providers'); setOpOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">Fornecedores</button>
                </div>
              )}
            </div>

            {/* Dispositivos (movido pro final; agrega Backups) */}
            <div onMouseLeave={() => setDmOpen(false)} className="relative">
              <button onMouseEnter={() => setDmOpen(true)} onClick={() => setDmOpen((v) => !v)}
                className={navBtn(['vendors', 'models', 'catalog', 'devices', 'pops', 'backups', 'firmware'].includes(route))}>
                Dispositivos ▾
              </button>
              {dmOpen && (
                <div className="absolute top-full left-0 bg-slate-900 ring-1 ring-slate-800 rounded shadow-lg w-44 py-1 mt-0">
                  <button onClick={() => { go('catalog'); setDmOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">Catálogo (vendor/model)</button>
                  <button onClick={() => { go('models'); setDmOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">Modelos (lista global)</button>
                  <button onClick={() => { go('firmware'); setDmOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">Firmware</button>
                  <button onClick={() => { go('pops'); setDmOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">POPs</button>
                  <button onClick={() => { go('devices'); setDmOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">Hosts</button>
                  <button onClick={() => { go('backups'); setDmOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">Backups</button>
                </div>
              )}
            </div>

            {/* Admin — usuários e grupos (ADMIN-only) */}
            {isAdmin && (
              <div onMouseLeave={() => setAdOpen(false)} className="relative">
                <button onMouseEnter={() => setAdOpen(true)} onClick={() => setAdOpen((v) => !v)}
                  className={navBtn(['users', 'groups', 'workers'].includes(route))}>
                  Admin ▾
                </button>
                {adOpen && (
                  <div className="absolute top-full left-0 bg-slate-900 ring-1 ring-slate-800 rounded shadow-lg w-44 py-1 mt-0">
                    <button onClick={() => { go('users'); setAdOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">Usuários</button>
                    <button onClick={() => { go('groups'); setAdOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">Grupos</button>
                    <button onClick={() => { go('workers'); setAdOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-800">Workers</button>
                  </div>
                )}
              </div>
            )}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <HealthBadge data={healthQ.data} />
            <FirmwareDeployBadge items={activeDeploys} />
            <button onClick={() => setChangingPw(true)} className="text-xs text-slate-400 hover:text-emerald-400" title={`Trocar minha senha (${user.email})`}>
              {user.name || user.email}
            </button>
            <button onClick={toggleTheme} title={theme === 'dark' ? 'Mudar pra tema claro' : 'Mudar pra tema escuro'}
                    className="text-slate-400 hover:text-emerald-400 text-base leading-none px-1">
              {theme === 'dark' ? '☀' : '☾'}
            </button>
            <button onClick={toggleFull}
                    title={fsSupported
                      ? (isFull ? 'Sair de tela cheia' : 'Tela cheia')
                      : 'Modo foco — esconde o cabeçalho (o iOS não permite esconder a barra do navegador)'}
                    className="text-slate-400 hover:text-emerald-400 text-base leading-none px-1">
              {isFull || focusMode ? '⤡' : '⛶'}
            </button>
            <button onClick={() => logout.mutate()} className="text-xs text-slate-400 hover:text-rose-400">sair</button>
          </div>
        </div>
      </header>

      <main className={route === 'topo3d' ? 'w-full flex-1' : 'max-w-7xl mx-auto w-full px-4 py-6 space-y-6 flex-1'}>
        {route === 'topo3d' && (
          <Suspense fallback={<div className="p-6 text-slate-400">carregando 3D…</div>}>
            <Topo3DPage />
          </Suspense>
        )}
        {route === 'dashboard' && <Dashboard />}
        {route === 'vendors' && <VendorsPage canWrite={isAdmin} />}
        {route === 'models' && <ModelsPage canWrite={isAdmin} />}
        {route === 'catalog' && (
          <Suspense fallback={<div className="p-6 text-slate-400">carregando catálogo…</div>}>
            <CatalogPage user={user} subRoute={subRoute} />
          </Suspense>
        )}
        {route === 'devices' && <DevicesPage canWrite={isAdmin} initialTab={(subRoute as DeviceTab) || undefined} />}
        {route === 'olts' && <DevicesPage canWrite={isAdmin} initialRole="OLT" />}
        {route === 'onus' && (
          <Suspense fallback={<div className="p-6 text-slate-400">carregando overview ONUs…</div>}>
            <OnusOverviewPage />
          </Suspense>
        )}
        {route === 'onu-signal' && (
          <Suspense fallback={<div className="p-6 text-slate-400">carregando saúde de sinal…</div>}>
            <OnuSignalPage />
          </Suspense>
        )}
        {route === 'onu-flapping' && (
          <Suspense fallback={<div className="p-6 text-slate-400">carregando flapping…</div>}>
            <OnuFlappingPage />
          </Suspense>
        )}
        {route === 'onu-sanitation' && (
          <Suspense fallback={<div className="p-6 text-slate-400">carregando saneamento…</div>}>
            <OnuSanitationPage />
          </Suspense>
        )}
        {route === 'health' && (
          <Suspense fallback={<div className="p-6 text-slate-400">carregando saúde…</div>}>
            <HealthPage />
          </Suspense>
        )}
        {route === 'pops' && <PopsPage canWrite={isAdmin} />}
        {route === 'bgp' && (subRoute === 'massa' ? <BgpBulkPage /> : subRoute ? <BgpDeviceDetail deviceId={Number(subRoute)} /> : <BgpOverviewPage />)}
        {route === 'optical' && (subRoute ? <OpticalIfaceDetail ifaceId={Number(subRoute)} /> : <OpticalOverviewPage />)}
        {route === 'agente-noc' && <NocAgentePage subRoute={subRoute} isAdmin={isAdmin} />}
        {route === 'agente-atendimento' && <AtendimentoAgentePage subRoute={subRoute} />}
        {route === "map" && <MapPage isAdmin={isAdmin} />}
        {route === 'lldp' && <LldpPage canWrite={user.role !== 'VIEWER'} />}
        {route === 'mpls' && <MplsPage user={user} subRoute={subRoute} />}
        {route === 'events' && <EventsPage />}
        {route === 'backups' && (
          <Suspense fallback={<div className="p-6 text-slate-400">carregando…</div>}>
            <BackupsPage user={user} subRoute={subRoute} />
          </Suspense>
        )}
        {route === 'firmware' && (
          <Suspense fallback={<div className="p-6 text-slate-400">carregando…</div>}>
            <FirmwarePage user={user} subRoute={subRoute} />
          </Suspense>
        )}
        {route === 'device' && (
          <Suspense fallback={<div className="p-6 text-slate-400">carregando device…</div>}>
            <DevicePage user={user} subRoute={subRoute} />
          </Suspense>
        )}
        {route === 'cto' && (
          <Suspense fallback={<div className="p-6 text-slate-400">carregando limpeza de CTO…</div>}>
            <CtoWatchPage user={user} subRoute={subRoute} />
          </Suspense>
        )}
        {route === 'providers' && (
          <Suspense fallback={<div className="p-6 text-slate-400">carregando…</div>}>
            <ProvidersPage user={user} subRoute={subRoute} />
          </Suspense>
        )}
        {route === 'users' && isAdmin && (
          <Suspense fallback={<div className="p-6 text-slate-400">carregando…</div>}>
            <UsersPage currentUser={user} />
          </Suspense>
        )}
        {route === 'groups' && isAdmin && (
          <Suspense fallback={<div className="p-6 text-slate-400">carregando…</div>}>
            <GroupsPage />
          </Suspense>
        )}
        {route === 'workers' && isAdmin && <WorkersPage />}
      </main>

      {changingPw && <MyProfileModal user={user} onClose={() => setChangingPw(false)} />}
    </div>
  );
}

function MyProfileModal({ user, onClose }: { user: User; onClose: () => void }) {
  const qc = useQueryClient();

  // -- seção dados pessoais (nome + telefone)
  const [name, setName] = useState(user.name ?? '');
  const [phone, setPhone] = useState(user.phone ?? '');
  const [profBusy, setProfBusy] = useState(false);
  const [profMsg, setProfMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const saveProfile = async () => {
    setProfMsg(null);
    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();
    if (trimmedName.length > 160) { setProfMsg({ kind: 'err', text: 'nome muito longo (máx. 160 caracteres)' }); return; }
    if (trimmedPhone.length > 40) { setProfMsg({ kind: 'err', text: 'telefone muito longo (máx. 40 caracteres)' }); return; }
    const nextName = trimmedName || null;
    const nextPhone = trimmedPhone || null;
    if (nextName === (user.name ?? null) && nextPhone === (user.phone ?? null)) {
      setProfMsg({ kind: 'err', text: 'nada mudou' });
      return;
    }
    setProfBusy(true);
    try {
      await api.updateMyProfile({ name: nextName, phone: nextPhone });
      await qc.invalidateQueries({ queryKey: ['me'] });
      setProfMsg({ kind: 'ok', text: 'perfil atualizado' });
    } catch (e: any) {
      const fe = e?.detail?.detail?.fieldErrors as Record<string, string[]> | undefined;
      if (fe && Object.keys(fe).length) setProfMsg({ kind: 'err', text: Object.entries(fe).map(([f, m]) => `${f}: ${m.join(', ')}`).join(' · ') });
      else setProfMsg({ kind: 'err', text: e?.detail?.error ?? e?.message ?? 'erro' });
    } finally { setProfBusy(false); }
  };

  // -- seção Senha
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwErr, setPwErr] = useState<string | null>(null);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwDone, setPwDone] = useState(false);
  const savePw = async () => {
    setPwErr(null);
    if (!current) return setPwErr('informe a senha atual');
    if (next.length < 6) return setPwErr('a nova senha deve ter no mínimo 6 caracteres');
    if (next !== confirmPw) return setPwErr('a confirmação não bate com a nova senha');
    if (next === current) return setPwErr('a nova senha precisa ser diferente da atual');
    setPwBusy(true);
    try {
      await api.changeMyPassword(current, next);
      setPwDone(true);
      setCurrent(''); setNext(''); setConfirmPw('');
    } catch (e: any) {
      if (e?.detail?.error === 'invalid_current_password') setPwErr('senha atual incorreta');
      else {
        const fe = e?.detail?.detail?.fieldErrors as Record<string, string[]> | undefined;
        if (fe && Object.keys(fe).length) setPwErr(Object.entries(fe).map(([f, m]) => `${f}: ${m.join(', ')}`).join(' · '));
        else setPwErr(e?.detail?.error ?? e?.message ?? 'erro');
      }
    } finally { setPwBusy(false); }
  };

  return (
    <Modal title={`Meu perfil — ${user.email}`} onClose={onClose}>
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wide text-emerald-400">Dados pessoais</div>
        <FormRow label="Nome">
          <input
            type="text"
            className={inp}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Como você quer aparecer no header"
            maxLength={160}
          />
        </FormRow>
        <FormRow label="Telefone">
          <input
            type="tel"
            className={inp}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+55 82 99999-9999"
            maxLength={40}
          />
        </FormRow>
        {profMsg && (
          <div className={`text-sm ${profMsg.kind === 'ok' ? 'text-emerald-300' : 'text-rose-400'}`}>{profMsg.text}</div>
        )}
        <div className="flex justify-end">
          <button onClick={saveProfile} disabled={profBusy} className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-xs font-semibold">
            {profBusy ? 'salvando...' : 'salvar perfil'}
          </button>
        </div>
      </div>

      <div className="border-t border-slate-800 pt-3 space-y-2">
        <div className="text-xs uppercase tracking-wide text-emerald-400">Trocar senha</div>
        {pwDone ? (
          <div className="text-sm text-emerald-300 bg-emerald-500/10 ring-1 ring-emerald-500/40 rounded p-3">
            Senha alterada com sucesso. Próximo login usará a nova.
          </div>
        ) : (
          <>
            <FormRow label="Senha atual *">
              <input type="password" className={inp} value={current} onChange={(e) => setCurrent(e.target.value)} />
            </FormRow>
            <FormRow label="Nova senha *">
              <input type="password" className={inp} value={next} onChange={(e) => setNext(e.target.value)} placeholder="mín. 6 caracteres" />
            </FormRow>
            <FormRow label="Confirmar nova senha *">
              <input type="password" className={inp} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} />
            </FormRow>
            {pwErr && <div className="text-rose-400 text-sm">{pwErr}</div>}
            <div className="flex justify-end">
              <button onClick={savePw} disabled={pwBusy} className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-xs font-semibold">
                {pwBusy ? 'salvando...' : 'alterar senha'}
              </button>
            </div>
          </>
        )}
      </div>

      <div className="flex justify-end pt-3 border-t border-slate-800">
        <button onClick={onClose} className="px-3 py-1.5 rounded text-sm text-slate-400 hover:text-slate-200">fechar</button>
      </div>
    </Modal>
  );
}

const navBtn = (active: boolean) =>
  `px-3 py-1.5 rounded text-sm ${active ? 'bg-slate-800 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'}`;

// ============================== DASHBOARD ==============================
function Dashboard() {
  const sumQ = useQuery({ queryKey: ['summary'], queryFn: api.summary });
  if (!sumQ.data) return <Splash msg="carregando..." />;
  const s = sumQ.data;
  return (
    <section className="space-y-4">
      <h1 className="text-xl font-semibold">Visão geral</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="Total devices" value={s.total} />
        <Card label="Alcançáveis" value={s.reachable} accent="emerald" />
        <Card label="Vendors" value={Object.keys(s.byVendor).length} />
        <Card label="Models" value={Object.keys(s.byModel).length} />
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <BreakdownCard title="Por vendor" data={s.byVendor} />
        <BreakdownCard title="Por role" data={s.byRole} />
        <BreakdownCard title="Por modelo" data={Object.fromEntries(Object.entries(s.byModel).sort((a,b)=>b[1]-a[1]))} />
      </div>
    </section>
  );
}

function Card({ label, value, accent = 'slate' }: { label: string; value: number; accent?: 'slate' | 'emerald' }) {
  return (
    <div className="bg-slate-900 rounded ring-1 ring-slate-800 p-3">
      <div className="text-xs text-slate-400">{label}</div>
      <div className={`text-2xl font-bold ${accent === 'emerald' ? 'text-emerald-400' : 'text-slate-100'}`}>{value}</div>
    </div>
  );
}

function BreakdownCard({ title, data }: { title: string; data: Record<string, number> }) {
  return (
    <div className="bg-slate-900 rounded ring-1 ring-slate-800 p-3">
      <div className="text-xs text-slate-400 mb-2">{title}</div>
      <div className="space-y-1">
        {Object.entries(data).map(([k, v]) => (
          <div key={k} className="flex items-center justify-between text-sm">
            <span className="text-slate-300 truncate">{k}</span>
            <span className="font-mono text-emerald-400">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================== VENDORS ==============================
function VendorsPage({ canWrite }: { canWrite: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['vendors'], queryFn: api.vendors });
  const [editing, setEditing] = useState<Vendor | null>(null);
  const [creating, setCreating] = useState(false);
  const del = useMutation({
    mutationFn: (id: number) => api.vendorDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendors'] }),
    onError: (e: any) => alert(deleteErrMsg(e))
  });
  return (
    <Section title="Vendors" sub={`${q.data?.vendors.length ?? 0} fabricantes`} action={canWrite && <PrimaryBtn onClick={() => setCreating(true)}>+ Novo vendor</PrimaryBtn>}>
      <table className="w-full text-xs">
        <thead className="text-slate-400">
          <tr className="border-b border-slate-800">
            <Th>Code</Th><Th>Nome</Th><Th>Descrição</Th><Th>Models</Th><Th>Devices</Th><Th>Enabled</Th><Th className="w-32">Ações</Th>
          </tr>
        </thead>
        <tbody>
          {q.data?.vendors.map((v) => (
            <tr key={v.id} className="border-b border-slate-800 hover:bg-slate-800/30">
              <Td><code className="text-emerald-400">{v.code}</code></Td>
              <Td>{v.name}</Td>
              <Td className="text-slate-500">{v.description ?? '—'}</Td>
              <Td>{v._count?.models ?? '—'}</Td>
              <Td>{v._count?.devices ?? '—'}</Td>
              <Td>{v.enabled ? '✓' : '✗'}</Td>
              <Td>
                {canWrite && <ActionBtn onClick={() => setEditing(v)}>editar</ActionBtn>}
                {canWrite && <ActionBtn variant="danger" onClick={() => confirmDelete('vendor', v.code) && del.mutate(v.id)}>x</ActionBtn>}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
      {(editing || creating) && (
        <VendorForm vendor={editing ?? undefined} onClose={() => { setEditing(null); setCreating(false); }} />
      )}
    </Section>
  );
}

function VendorForm({ vendor, onClose }: { vendor?: Vendor; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ code: vendor?.code ?? '', name: vendor?.name ?? '', description: vendor?.description ?? '', enabled: vendor?.enabled ?? true });
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => vendor ? api.vendorUpdate(vendor.id, form) : api.vendorCreate(form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['vendors'] }); onClose(); },
    onError: (e: any) => setErr(e.detail?.error === 'duplicate_code' ? 'code já existe' : (e.message ?? 'erro'))
  });
  return (
    <Modal title={vendor ? `Editar vendor ${vendor.code}` : 'Novo vendor'} onClose={onClose}>
      <FormRow label="Code (A-Z 0-9 _ -)"><input className={inp} disabled={!!vendor} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} /></FormRow>
      <FormRow label="Nome"><input className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormRow>
      <FormRow label="Descrição"><input className={inp} value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormRow>
      <FormRow label="Habilitado"><input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} /></FormRow>
      {err && <div className="text-rose-400 text-sm">{err}</div>}
      <FormActions onClose={onClose} onSave={() => { setErr(null); save.mutate(); }} pending={save.isPending} />
    </Modal>
  );
}

// ============================== MODELS ==============================
function ModelsPage({ canWrite }: { canWrite: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['models'], queryFn: () => api.models() });
  const vQ = useQuery({ queryKey: ['vendors'], queryFn: api.vendors });
  const [editing, setEditing] = useState<Model | null>(null);
  const [creating, setCreating] = useState(false);
  const del = useMutation({
    mutationFn: (id: number) => api.modelDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['models'] }),
    onError: (e: any) => alert(deleteErrMsg(e))
  });
  return (
    <Section title="Modelos" sub={`${q.data?.models.length ?? 0} modelos cadastrados`} action={canWrite && <PrimaryBtn onClick={() => setCreating(true)}>+ Novo modelo</PrimaryBtn>}>
      <table className="w-full text-xs">
        <thead className="text-slate-400"><tr className="border-b border-slate-800">
          <Th>Vendor</Th><Th>Code</Th><Th>Family</Th><Th>Driver class</Th><Th>Devices</Th><Th>Descrição</Th><Th className="w-32">Ações</Th>
        </tr></thead>
        <tbody>
          {q.data?.models.map((m) => (
            <tr key={m.id} className="border-b border-slate-800 hover:bg-slate-800/30">
              <Td><code className="text-emerald-400">{m.vendor?.code}</code></Td>
              <Td><code>{m.code}</code></Td>
              <Td className="text-slate-400">{m.family ?? '—'}</Td>
              <Td className="text-emerald-400">{m.driverClass}</Td>
              <Td>{m._count?.devices ?? 0}</Td>
              <Td className="text-slate-500">{m.description ?? '—'}</Td>
              <Td>
                {canWrite && <ActionBtn onClick={() => setEditing(m)}>editar</ActionBtn>}
                {canWrite && <ActionBtn variant="danger" onClick={() => confirmDelete('model', m.code) && del.mutate(m.id)}>x</ActionBtn>}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
      {(editing || creating) && (
        <ModelForm model={editing ?? undefined} vendors={vQ.data?.vendors ?? []} onClose={() => { setEditing(null); setCreating(false); }} />
      )}
    </Section>
  );
}

function ModelForm({ model, vendors, onClose }: { model?: Model; vendors: Vendor[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    vendorId: model?.vendorId ?? model?.vendor?.id ?? vendors[0]?.id ?? 0,
    code: model?.code ?? '',
    family: model?.family ?? '',
    driverClass: model?.driverClass ?? 'generic-cli',
    description: model?.description ?? '',
    enabled: model?.enabled ?? true
  });
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => model ? api.modelUpdate(model.id, form) : api.modelCreate(form as any),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['models'] }); onClose(); },
    onError: (e: any) => setErr(e.detail?.error ?? e.message ?? 'erro')
  });
  return (
    <Modal title={model ? `Editar modelo ${model.code}` : 'Novo modelo'} onClose={onClose}>
      <FormRow label="Vendor">
        <select className={inp} value={form.vendorId} onChange={(e) => setForm({ ...form, vendorId: Number(e.target.value) })}>
          {vendors.map((v) => <option key={v.id} value={v.id}>{v.code}</option>)}
        </select>
      </FormRow>
      <FormRow label="Code"><input className={inp} disabled={!!model} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></FormRow>
      <FormRow label="Family"><input className={inp} value={form.family ?? ''} onChange={(e) => setForm({ ...form, family: e.target.value })} /></FormRow>
      <FormRow label="Driver class"><input className={inp} value={form.driverClass} onChange={(e) => setForm({ ...form, driverClass: e.target.value })} /></FormRow>
      <FormRow label="Descrição"><input className={inp} value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormRow>
      <FormRow label="Habilitado"><input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} /></FormRow>
      {err && <div className="text-rose-400 text-sm">{err}</div>}
      <FormActions onClose={onClose} onSave={() => { setErr(null); save.mutate(); }} pending={save.isPending} />
    </Modal>
  );
}

// ============================== WORKERS (Admin) ==============================
// Tabela dinâmica com manifest do backend + jobs counts (waiting/active/...)
// + workersAlive (BullMQ Queue.getWorkers). Auto-refresh 5s.
function WorkersPage() {
  const [errFilter, setErrFilter] = useState<string>('ALL');
  const q = useQuery({
    queryKey: ['admin-workers'],
    queryFn: api.adminWorkers,
    refetchInterval: 5_000
  });
  const errQ = useQuery({
    queryKey: ['admin-worker-errors', errFilter],
    queryFn: () => api.adminWorkerErrors({ queue: errFilter, limit: errFilter === 'ALL' ? 100 : 200 }),
    refetchInterval: 10_000
  });
  if (!q.data) return <Splash msg="carregando..." />;
  const rows = q.data.rows;
  const alive = rows.reduce((acc, r) => acc + r.workersAlive, 0);
  const active = rows.reduce((acc, r) => acc + r.jobs.active, 0);
  const waiting = rows.reduce((acc, r) => acc + r.jobs.waiting, 0);
  const failed = rows.reduce((acc, r) => acc + r.jobs.failed, 0);

  const kindBadge = (k: string) =>
    k === 'cron' ? 'bg-sky-500/15 ring-sky-600/60 text-sky-300' :
    k === 'tail' ? 'bg-amber-500/15 ring-amber-600/60 text-amber-300' :
    'bg-fuchsia-500/15 ring-fuchsia-600/60 text-fuchsia-300';

  const num = (n: number, color = 'text-slate-300') =>
    n === 0 ? <span className="text-slate-600">0</span> : <span className={color}>{n}</span>;

  return (
    <section className="space-y-4">
      <div className="flex items-baseline gap-3">
        <h1 className="text-xl font-semibold">Workers</h1>
        <span className="text-xs text-slate-500">auto-refresh 5s · {rows.length} workers</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card label="Workers" value={rows.length} />
        <Card label="Workers vivos" value={alive} accent={alive >= rows.length ? 'emerald' : undefined} />
        <Card label="Jobs ativos" value={active} />
        <Card label="Jobs em fila" value={waiting} />
        <Card label="Jobs falhos" value={failed} />
      </div>

      <Section title="Inventário" sub={`origem: manifest em apps/api/src/routes/admin-workers.ts`}>
        <div className="overflow-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-400 sticky top-0 bg-slate-900">
              <tr className="border-b border-slate-800">
                <Th>Nome</Th>
                <Th>Tipo</Th>
                <Th>Período</Th>
                <Th className="text-center">Conc.</Th>
                <Th className="text-center" title="Workers Node consumindo a fila agora">Vivos</Th>
                <Th className="text-center" title="Jobs em execução">Active</Th>
                <Th className="text-center" title="Jobs esperando processamento">Wait</Th>
                <Th className="text-center" title="Jobs agendados (delay/repeat)">Delay</Th>
                <Th className="text-center" title="Jobs falhos (não retentados)">Fail</Th>
                <Th className="text-center" title="Histórico mantido em Redis (removeOnComplete)">Done</Th>
                <Th>Trigger</Th>
                <Th>Origem dos dados</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.queueName} className="border-b border-slate-800 hover:bg-slate-800/30 align-top">
                  <td className="py-1.5 pr-2">
                    <div className="font-mono text-slate-200">{r.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{r.queueName}</div>
                    <div className="text-[10px] text-slate-500 italic max-w-md truncate" title={r.description}>{r.description}</div>
                  </td>
                  <td className="py-1.5 pr-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ring-1 ${kindBadge(r.kind)}`}>{r.kind}</span>
                  </td>
                  <td className="py-1.5 pr-2 text-slate-300">{r.schedule}</td>
                  <td className="py-1.5 pr-2 text-center text-slate-400">{r.concurrency}</td>
                  <td className="py-1.5 pr-2 text-center">
                    {r.workersAlive > 0
                      ? <span className="text-emerald-400" title={`${r.workersAlive} worker(s) conectado(s)`}>● {r.workersAlive}</span>
                      : <span className="text-rose-500" title="Nenhum worker conectado a essa fila">● 0</span>}
                  </td>
                  <td className="py-1.5 pr-2 text-center">{num(r.jobs.active, 'text-emerald-300')}</td>
                  <td className="py-1.5 pr-2 text-center">{num(r.jobs.waiting, 'text-sky-300')}</td>
                  <td className="py-1.5 pr-2 text-center">{num(r.jobs.delayed, 'text-amber-300')}</td>
                  <td className="py-1.5 pr-2 text-center">
                    {r.jobs.failed > 0
                      ? <button onClick={() => { setErrFilter(r.queueName); document.getElementById('worker-errors-section')?.scrollIntoView({ behavior: 'smooth' }); }}
                          className="text-rose-400 hover:text-rose-300 underline-offset-2 hover:underline" title="ver erros deste worker">
                          {r.jobs.failed}
                        </button>
                      : <span className="text-slate-600">0</span>}
                  </td>
                  <td className="py-1.5 pr-2 text-center text-slate-500">{r.jobs.completed}</td>
                  <td className="py-1.5 pr-2 text-slate-300 max-w-xs">{r.trigger}</td>
                  <td className="py-1.5 pr-2 text-slate-400 font-mono text-[10px] max-w-xs">{r.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-[10px] text-slate-500 flex flex-wrap gap-3">
          <span>● <span className="text-emerald-400">verde</span> = worker conectado ao Redis</span>
          <span>● <span className="text-rose-500">vermelho</span> = sem worker (cuidado se kind=cron/tail)</span>
          <span>Done = histórico em Redis (cap por removeOnComplete)</span>
        </div>
      </Section>

      <div id="worker-errors-section">
        <WorkerErrorsSection rows={rows} filter={errFilter} setFilter={setErrFilter} data={errQ.data} />
      </div>
    </section>
  );
}

// Lista de failed jobs por worker (Queue.getFailed). Auto-refresh 10s.
function WorkerErrorsSection({ rows, filter, setFilter, data }: {
  rows: Array<{ name: string; queueName: string; jobs: { failed: number } }>;
  filter: string;
  setFilter: (q: string) => void;
  data: { errors: Array<any> } | undefined;
}) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const errs = data?.errors ?? [];
  const fmtTs = (t: number | null) => t ? new Date(t).toLocaleString() : '—';
  const truncate = (s: string | null, n: number) => !s ? '' : s.length > n ? s.slice(0, n) + '…' : s;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-worker-errors'] });
    qc.invalidateQueries({ queryKey: ['admin-workers'] });
  };

  const cleanAll = useMutation({
    mutationFn: () => api.adminWorkerErrorsClean(filter),
    onSuccess: (r) => { setMsg({ ok: true, text: `✓ ${r.total} erro(s) removido(s)` }); invalidate(); },
    onError: (e: any) => setMsg({ ok: false, text: e.detail?.error ?? e.message ?? 'erro' })
  });
  const retryAll = useMutation({
    mutationFn: () => api.adminWorkerErrorsRetry(filter),
    onSuccess: (r) => { setMsg({ ok: true, text: `✓ ${r.total} job(s) re-enfileirado(s)` }); invalidate(); },
    onError: (e: any) => setMsg({ ok: false, text: e.detail?.error ?? e.message ?? 'erro' })
  });
  const retryOne = useMutation({
    mutationFn: ({ queue, jobId }: { queue: string; jobId: string }) => api.adminWorkerErrorsRetry(queue, jobId),
    onSuccess: (r) => { setMsg({ ok: true, text: `✓ job ${r.jobId} re-enfileirado` }); invalidate(); },
    onError: (e: any) => setMsg({ ok: false, text: e.detail?.error ?? e.message ?? 'erro' })
  });

  const askClean = () => {
    const scope = filter === 'ALL' ? `TODOS os workers (${errs.length} mostrados, pode haver mais)` : `o worker ${filter}`;
    if (confirm(`Remover todos os failed jobs de ${scope}?\nIsto é destrutivo — histórico de erros é perdido.`)) cleanAll.mutate();
  };
  const askRetry = () => {
    const scope = filter === 'ALL' ? `TODOS os workers` : `o worker ${filter}`;
    if (confirm(`Re-enfileirar todos os failed jobs de ${scope}?\nVão tentar de novo agora.`)) retryAll.mutate();
  };

  return (
    <Section title="Erros recentes" sub={`fonte: BullMQ failed jobs · auto-refresh 10s · ${errs.length} mostrados`}>
      <div className="flex flex-wrap items-center gap-2 mb-3 -mt-1">
        <label className="text-xs text-slate-400">filtrar por worker:</label>
        <select value={filter} onChange={(e) => { setFilter(e.target.value); setMsg(null); }}
          className="bg-slate-950 ring-1 ring-slate-800 rounded px-2 py-1 text-xs">
          <option value="ALL">— todos —</option>
          {rows.map((r) => (
            <option key={r.queueName} value={r.queueName}>
              {r.name} {r.jobs.failed > 0 ? `(${r.jobs.failed} fail)` : ''}
            </option>
          ))}
        </select>
        {filter !== 'ALL' && (
          <button onClick={() => setFilter('ALL')} className="text-xs text-slate-400 hover:text-slate-200">× limpar filtro</button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={askRetry} disabled={errs.length === 0 || retryAll.isPending}
            className="px-2.5 py-1 rounded bg-sky-700 hover:bg-sky-600 disabled:opacity-40 text-xs font-semibold"
            title={`Retry todos failed ${filter === 'ALL' ? '(todos workers)' : `do ${filter}`}`}>
            {retryAll.isPending ? '…' : '↻ retry todos'}
          </button>
          <button onClick={askClean} disabled={errs.length === 0 || cleanAll.isPending}
            className="px-2.5 py-1 rounded bg-rose-700 hover:bg-rose-600 disabled:opacity-40 text-xs font-semibold"
            title={`Limpar histórico de erros ${filter === 'ALL' ? '(todos workers)' : `do ${filter}`}`}>
            {cleanAll.isPending ? '…' : '✕ limpar erros'}
          </button>
        </div>
        <div className="basis-full"></div>
        {msg && (
          <div className={`text-[11px] ${msg.ok ? 'text-emerald-400' : 'text-rose-400'}`}>{msg.text}</div>
        )}
        <span className="ml-auto text-[10px] text-slate-500">
          {filter === 'ALL' ? 'agregando últimos 20/worker' : `limit 200 do worker ${filter}`}
        </span>
      </div>
      {errs.length === 0 ? (
        <div className="text-xs text-emerald-400/80 px-2 py-3">✓ nenhum erro recente {filter === 'ALL' ? 'em nenhum worker' : 'neste worker'}.</div>
      ) : (
        <div className="overflow-auto max-h-[32rem] border border-slate-800 rounded">
          <table className="w-full text-xs">
            <thead className="text-slate-400 bg-slate-900 sticky top-0">
              <tr className="border-b border-slate-800">
                <Th>Worker</Th>
                <Th>Quando</Th>
                <Th className="text-center">Tent.</Th>
                <Th>Job</Th>
                <Th>Erro</Th>
                <Th className="w-12"></Th>
              </tr>
            </thead>
            <tbody>
              {errs.map((e) => {
                const isOpen = expanded === `${e.queueName}/${e.jobId}`;
                return (
                  <Fragment key={`${e.queueName}/${e.jobId}`}>
                    <tr className="border-b border-slate-800/50 hover:bg-slate-800/30 align-top">
                      <td className="py-1 pr-2 font-mono text-[10px] text-sky-300">{e.workerName}</td>
                      <td className="py-1 pr-2 text-slate-400 text-[10px]" title={`criado: ${fmtTs(e.timestamp)} · processado: ${fmtTs(e.processedOn)}`}>
                        {fmtTs(e.finishedOn ?? e.timestamp)}
                      </td>
                      <td className="py-1 pr-2 text-center text-amber-400 text-[10px]">{e.attemptsMade ?? '—'}</td>
                      <td className="py-1 pr-2 font-mono text-[10px] text-slate-500 max-w-[10rem] truncate" title={`${e.jobName} #${e.jobId}`}>
                        {e.jobName} <span className="text-slate-600">#{e.jobId}</span>
                      </td>
                      <td className="py-1 pr-2 text-rose-300 max-w-[40rem] truncate" title={e.failedReason ?? ''}>
                        {truncate(e.failedReason, 120)}
                      </td>
                      <td className="py-1 pr-2 whitespace-nowrap">
                        <button onClick={() => retryOne.mutate({ queue: e.queueName, jobId: e.jobId })}
                          disabled={retryOne.isPending}
                          className="text-[10px] text-sky-400 hover:underline disabled:opacity-40 mr-2"
                          title="retry deste job">↻</button>
                        <button onClick={() => setExpanded(isOpen ? null : `${e.queueName}/${e.jobId}`)}
                          className="text-[10px] text-sky-400 hover:underline">{isOpen ? '▾' : '▸'}</button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b border-slate-800 bg-slate-950/60">
                        <td colSpan={6} className="px-3 py-2 space-y-2">
                          {e.data != null && (
                            <div>
                              <div className="text-[10px] text-slate-400 mb-1">data:</div>
                              <pre className="text-[10px] text-slate-300 bg-slate-900/60 p-2 rounded overflow-x-auto">{JSON.stringify(e.data, null, 2)}</pre>
                            </div>
                          )}
                          {e.failedReason && (
                            <div>
                              <div className="text-[10px] text-slate-400 mb-1">failedReason completo:</div>
                              <pre className="text-[10px] text-rose-300 bg-slate-900/60 p-2 rounded overflow-x-auto whitespace-pre-wrap">{e.failedReason}</pre>
                            </div>
                          )}
                          {e.stacktrace && (
                            <div>
                              <div className="text-[10px] text-slate-400 mb-1">stacktrace:</div>
                              <pre className="text-[10px] text-slate-400 bg-slate-900/60 p-2 rounded overflow-x-auto whitespace-pre-wrap">{e.stacktrace}</pre>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-2 text-[10px] text-slate-500">
        Limpar histórico: BullMQ purga automático conforme <code>removeOnFail</code> de cada worker (500 jobs por padrão). Erros aqui = falhas que esgotaram <code>attempts</code>.
      </div>
    </Section>
  );
}

// ============================== DEVICES (Hosts) ==============================
// Banner colapsável com credenciais com falha (lastTestOk=false). Vem da própria
// devices list (sem endpoint extra) — o backend já expõe lastTest_* por access.
function FailingCredentialsBanner({ devices, onEdit }: { devices: any[]; onEdit: (d: any) => void }) {
  const [open, setOpen] = useState(false);
  const failing = devices.flatMap((d) =>
    (d.accesses ?? [])
      .filter((a: any) => a.lastTestOk === false)
      .map((a: any) => ({ device: d, access: a }))
  );
  if (failing.length === 0) return null;
  return (
    <div className="mb-3 -mt-1 rounded border border-rose-700/60 bg-rose-500/10">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-rose-500/15">
        <span className="text-rose-300 text-sm font-semibold">⚠ {failing.length} credencia{failing.length === 1 ? 'l' : 'is'} com falha</span>
        <span className="text-[10px] text-rose-400/80">— último teste por kind</span>
        <span className="ml-auto text-rose-300 text-xs">{open ? '▾ ocultar' : '▸ ver'}</span>
      </button>
      {open && (
        <div className="border-t border-rose-700/40 max-h-72 overflow-auto">
          <table className="w-full text-xs">
            <thead className="text-rose-300/80 bg-rose-500/10 sticky top-0">
              <tr>
                <th className="px-3 py-1 text-left">Host</th>
                <th className="px-3 py-1 text-left">Mgmt IP</th>
                <th className="px-3 py-1 text-left">Kind</th>
                <th className="px-3 py-1 text-left">Erro</th>
                <th className="px-3 py-1 text-left">Quando</th>
                <th className="px-3 py-1 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {failing.map(({ device, access }) => (
                <tr key={`${device.id}/${access.id}`} className="border-t border-rose-800/30 hover:bg-rose-500/10">
                  <td className="px-3 py-1 font-mono text-slate-200 max-w-[20rem] truncate" title={device.hostname}>{device.hostname}</td>
                  <td className="px-3 py-1 font-mono text-slate-400">{device.mgmtIp}</td>
                  <td className="px-3 py-1"><span className="px-1 rounded text-[10px] bg-rose-500/15 ring-1 ring-rose-600/60 text-rose-300">{access.kind}</span></td>
                  <td className="px-3 py-1 text-rose-300/90 max-w-[24rem] truncate" title={access.lastTestError ?? ''}>{access.lastTestError ?? 'erro'}</td>
                  <td className="px-3 py-1 text-slate-500 text-[10px]">{access.lastTestAt ? new Date(access.lastTestAt).toLocaleString() : '—'}</td>
                  <td className="px-3 py-1">
                    <button onClick={() => onEdit(device)} className="text-[10px] text-sky-400 hover:underline">editar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DevicesPage({ canWrite, initialRole = '', initialTab }: { canWrite: boolean; initialRole?: string; initialTab?: DeviceTab }) {
  const qc = useQueryClient();
  const [vendorCode, setVendorCode] = useState('');
  const [tab, setTab] = useState<DeviceTab>(initialTab ?? (initialRole === 'OLT' ? 'olt' : 'all'));
  const [q, setQ] = useState('');
  const vendorsQ = useQuery({ queryKey: ['vendors'], queryFn: api.vendors });
  // Busca todos os devices (≤500) que casam vendor+busca; as abas filtram client-side.
  const devicesQ = useQuery({
    queryKey: ['devices', vendorCode, q],
    queryFn: () => api.devices({ vendorCode, q, take: 500 })
  });
  const allDevices = devicesQ.data?.devices ?? [];
  const tabCounts = useMemo(() => {
    const c: Record<DeviceTab, number> = { all: 0, router: 0, olt: 0, bgp: 0, rtf: 0, fail: 0 };
    for (const t of DEVICE_TABS) c[t.key] = allDevices.filter((d) => matchDeviceTab(d, t.key)).length;
    return c;
  }, [allDevices]);
  const devices = useMemo(() => allDevices.filter((d) => matchDeviceTab(d, tab)), [allDevices, tab]);
  const [editing, setEditing] = useState<Device | null>(null);
  const [creating, setCreating] = useState(false);
  const del = useMutation({
    mutationFn: ({ id, confirm }: { id: number; confirm: boolean }) => api.deviceDelete(id, confirm),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devices'] }),
    onError: (e: any) => {
      if (e.detail?.error === 'needs_confirm') {
        if (confirm(`Deletar device removerá em cascade:\n` + JSON.stringify(e.detail.cascade, null, 2) + `\n\nConfirma?`)) {
          del.mutate({ id: e.detail.id, confirm: true } as any); // re-enviar com confirm
        }
      } else alert(deleteErrMsg(e));
    }
  });

  return (
    <Section
      title="Hosts"
      sub={`${devices.length} de ${allDevices.length} dispositivos`}
      action={canWrite && (
        <div className="flex gap-2">
          <SyncZabbixBtn />
          <PrimaryBtn onClick={() => setCreating(true)}>+ Novo host</PrimaryBtn>
        </div>
      )}
    >
      {/* Abas por tipo de host (filtro client-side) */}
      <div className="flex flex-wrap gap-1 mb-3 border-b border-slate-800">
        {DEVICE_TABS.map((t) => {
          const active = tab === t.key;
          const alert = t.key === 'fail' && tabCounts.fail > 0;   // aba de falha em rosa qd há problemas
          const cls = active
            ? (alert ? 'border-rose-500 text-rose-300 font-medium' : 'border-emerald-500 text-emerald-300 font-medium')
            : (alert ? 'border-transparent text-rose-400/80 hover:text-rose-300' : 'border-transparent text-slate-400 hover:text-slate-200');
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 text-xs -mb-px border-b-2 transition-colors ${cls}`}>
              {t.label} <span className={`text-[10px] ${alert ? 'text-rose-400' : 'text-slate-500'}`}>({tabCounts[t.key]})</span>
            </button>
          );
        })}
      </div>
      <FailingCredentialsBanner devices={devices} onEdit={(d) => setEditing(d)} />
      <div className="flex flex-wrap items-center gap-2 mb-3 -mt-1">
        <input placeholder="buscar hostname/IP/desc..." value={q} onChange={(e) => setQ(e.target.value)}
          className="bg-slate-950 ring-1 ring-slate-800 rounded px-3 py-1.5 text-xs flex-1 min-w-[14rem] max-w-md focus:outline-none focus:ring-emerald-600" />
        <select value={vendorCode} onChange={(e) => setVendorCode(e.target.value)}
          className="bg-slate-950 ring-1 ring-slate-800 rounded px-2 py-1.5 text-xs">
          <option value="">todos vendors</option>
          {vendorsQ.data?.vendors.map((v) => <option key={v.code} value={v.code}>{v.code} ({v._count?.devices ?? 0})</option>)}
        </select>
      </div>
      <table className="w-full text-xs">
        <thead className="text-slate-400"><tr className="border-b border-slate-800">
          <Th>Hostname</Th><Th>Vendor</Th><Th>Model</Th><Th>Role</Th><Th>Mgmt IP</Th><Th>POP</Th><Th>Acessos</Th><Th className="w-32">Ações</Th>
        </tr></thead>
        <tbody>
          {devices.map((d) => (
            <tr key={d.id} className="border-b border-slate-800 hover:bg-slate-800/30">
              <Td className="font-mono text-slate-200 max-w-[20rem] truncate">
                <a href={`#device/${d.id}/dashboard`} className="text-slate-200 hover:text-emerald-400 hover:underline">{d.hostname}</a>
              </Td>
              <Td><code className="text-emerald-400">{d.vendor?.code}</code></Td>
              <Td>{d.model?.code ?? '—'}</Td>
              <Td>{d.role}</Td>
              <Td className="font-mono text-slate-400">{d.mgmtIp}:{d.port}</Td>
              <Td className="text-slate-400">{d.pop ? <span className="text-[10px]">{d.pop.uf && <code className="text-emerald-400 mr-1">{d.pop.uf}</code>}{d.pop.city ?? d.pop.name}</span> : '—'}</Td>
              <Td>{(d.accesses?.length ?? 0) === 0
                ? <span title="Nenhuma credencial cadastrada" className="px-1 rounded ring-1 text-[10px] bg-amber-500/15 ring-amber-600/60 text-amber-300">sem credencial</span>
                : d.accesses?.map((a: any) => {
                const tested = a.lastTestAt != null;
                const ok = a.lastTestOk === true;
                const cls = !tested ? 'bg-slate-800 ring-slate-700 text-slate-300' :
                            ok ? 'bg-emerald-500/15 ring-emerald-600/60 text-emerald-300' :
                            'bg-rose-500/15 ring-rose-600/60 text-rose-300';
                const title = !tested ? `${a.kind}: nunca testado` :
                              ok ? `${a.kind}: OK em ${new Date(a.lastTestAt).toLocaleString()}` :
                              `${a.kind}: falhou em ${new Date(a.lastTestAt).toLocaleString()} — ${a.lastTestError ?? 'erro'}`;
                return <span key={a.id} title={title} className={`mr-1 px-1 rounded ring-1 text-[10px] ${cls}`}>{a.kind}</span>;
              })}</Td>
              <Td>
                {/* F11 — atalhos fw/editar removidos: clique no hostname abre o DevicePage,
                    e a edição vive no botão "✏ Editar host" do header da página detail. */}
                {canWrite && <ActionBtn variant="danger" onClick={async () => {
                  if (!confirmDelete('host', d.hostname)) return;
                  try { await api.deviceDelete(d.id, false); qc.invalidateQueries({ queryKey: ['devices'] }); }
                  catch (e: any) {
                    if (e.detail?.error === 'needs_confirm') {
                      if (confirm('Apagar em cascade?\n' + JSON.stringify(e.detail.cascade, null, 2))) {
                        await api.deviceDelete(d.id, true);
                        qc.invalidateQueries({ queryKey: ['devices'] });
                      }
                    } else alert(deleteErrMsg(e));
                  }
                }}>x</ActionBtn>}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
      {(editing || creating) && (
        <DeviceForm device={editing ?? undefined} vendors={vendorsQ.data?.vendors ?? []} onClose={() => { setEditing(null); setCreating(false); }} />
      )}
    </Section>
  );
}

function SyncZabbixBtn() {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: (force: boolean) => api.zbxSyncHostIds(force),
    onSuccess: (r) => { setMsg(`${r.matched} vinculados (${r.byName} por nome, ${r.byIp} por IP)${r.conflicts ? ` · ${r.conflicts} conflitos` : ''}`); qc.invalidateQueries({ queryKey: ['devices'] }); },
    onError: (e: any) => setMsg(e.detail?.detail ?? e.detail?.error ?? e.message ?? 'erro no sync')
  });
  return (
    <div className="flex items-center gap-2">
      {msg && <span className="text-xs text-slate-400">{msg}</span>}
      <button onClick={() => { setMsg(null); m.mutate(false); }} disabled={m.isPending}
              className="px-3 py-1.5 text-sm bg-sky-700 hover:bg-sky-600 rounded disabled:opacity-50"
              title="Vincula devices sem zabbixHostId aos hosts do Zabbix (match por nome/IP)">
        {m.isPending ? 'sync…' : '↻ Sync Zabbix hostid'}
      </button>
    </div>
  );
}

export function DeviceForm({ device, vendors, onClose }: { device?: Device; vendors: Vendor[]; onClose: () => void }) {
  const qc = useQueryClient();
  const modelsQ = useQuery({ queryKey: ['models'], queryFn: () => api.models() });
  const popsQ = useQuery({ queryKey: ['pops'], queryFn: () => api.pops() });
  const deviceFullQ = useQuery({
    queryKey: ['device', device?.id],
    queryFn: () => api.device(device!.id),
    enabled: !!device?.id
  });
  const [form, setForm] = useState({
    vendorId: device?.vendorId ?? device?.vendor?.id ?? vendors[0]?.id ?? 0,
    modelId: device?.modelId ?? device?.model?.id ?? undefined,
    role: device?.role ?? 'ROUTER',
    hostname: device?.hostname ?? '',
    mgmtIp: device?.mgmtIp ?? '',
    port: device?.port ?? 22,
    popId: device?.popId ?? device?.pop?.id ?? undefined,
    asn: device?.asn ?? '',
    description: device?.description ?? '',
    monitored: device?.monitored ?? true,
    bgpEnabled: device?.bgpEnabled ?? false,
    zabbixHostId: device?.zabbixHostId ?? ''
  });
  const [err, setErr] = useState<string | null>(null);
  const availableModels = (modelsQ.data?.models ?? []).filter((m) => m.vendor?.id === form.vendorId);
  const save = useMutation({
    mutationFn: () => {
      const body: any = { ...form, asn: form.asn ? Number(form.asn) : undefined, zabbixHostId: form.zabbixHostId ? Number(form.zabbixHostId) : null };
      if (device) return api.deviceUpdate(device.id, body);
      return api.deviceCreate(body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['devices'] }); qc.invalidateQueries({ queryKey: ['summary'] }); onClose(); },
    onError: (e: any) => setErr(e.detail?.detail ?? e.detail?.error ?? e.message ?? 'erro')
  });
  return (
    <Modal title={device ? `Editar host ${device.hostname}` : 'Novo host'} onClose={onClose} wide>
      <div className="grid md:grid-cols-2 gap-3">
        <FormRow label="Hostname (único)"><input className={inp} value={form.hostname} onChange={(e) => setForm({ ...form, hostname: e.target.value })} /></FormRow>
        <FormRow label="Mgmt IP"><input className={inp} value={form.mgmtIp} onChange={(e) => setForm({ ...form, mgmtIp: e.target.value })} /></FormRow>
        <FormRow label="Port (SSH default)"><input className={inp} type="number" value={form.port} onChange={(e) => setForm({ ...form, port: Number(e.target.value) })} /></FormRow>
        <FormRow label="Role">
          <select className={inp} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as any })}>
            <option>ROUTER</option><option>SWITCH</option><option>OLT</option><option>FIREWALL</option><option>SERVER</option><option>OTHER</option>
          </select>
        </FormRow>
        <FormRow label="Vendor">
          <select className={inp} value={form.vendorId} onChange={(e) => setForm({ ...form, vendorId: Number(e.target.value), modelId: undefined })}>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.code}</option>)}
          </select>
        </FormRow>
        <FormRow label="Model">
          <Combobox
            options={availableModels.map((m: any) => ({
              id: m.id,
              label: m.code,
              aliases: [m.driverClass, m.vendor?.code].filter(Boolean).join(' ')
            }))}
            value={form.modelId ?? null}
            onChange={(id) => setForm({ ...form, modelId: id ?? undefined })}
            placeholder="— sem modelo — (digite pra buscar)"
          />
        </FormRow>
        <FormRow label="POP (site)">
          <Combobox
            options={(popsQ.data?.pops ?? []).map((p: any) => ({
              id: p.id,
              label: `${p.uf ? `[${p.uf}] ` : ''}${p.name}`,
              aliases: [p.code, p.city, p.uf].filter(Boolean).join(' ')
            }))}
            value={form.popId ?? null}
            onChange={(id) => setForm({ ...form, popId: id ?? undefined })}
            placeholder="— sem POP — (digite pra buscar)"
          />
        </FormRow>
        <FormRow label="Zabbix hostid"><input className={inp} type="number" value={form.zabbixHostId ?? ''} onChange={(e) => setForm({ ...form, zabbixHostId: e.target.value as any })} placeholder="id do host no Zabbix" /></FormRow>
        <FormRow label="ASN"><input className={inp} type="number" value={form.asn ?? ''} onChange={(e) => setForm({ ...form, asn: e.target.value as any })} /></FormRow>
        <FormRow label="Descrição" wide><input className={inp} value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormRow>
        <FormRow label="Monitored"><input type="checkbox" checked={form.monitored} onChange={(e) => setForm({ ...form, monitored: e.target.checked })} /></FormRow>
        <FormRow label="BGP"><input type="checkbox" checked={form.bgpEnabled} onChange={(e) => setForm({ ...form, bgpEnabled: e.target.checked })} /> <span className="text-xs text-slate-500 ml-2">marca pra coleta de BGP (peer discovery a cada 30s)</span></FormRow>
      </div>
      {err && <div className="text-rose-400 text-sm mt-2">{err}</div>}
      <FormActions onClose={onClose} onSave={() => { setErr(null); save.mutate(); }} pending={save.isPending} />

      {device && device.zabbixHostId && (
        <ZabbixItemsSection deviceId={device.id} hostid={device.zabbixHostId} />
      )}
      {device && deviceFullQ.data && (
        <AccessesSection deviceId={device.id} accesses={deviceFullQ.data.device.accesses ?? []} />
      )}
    </Modal>
  );
}

// ============================== ZABBIX ITEMS ==============================
function ZabbixItemsSection({ deviceId, hostid }: { deviceId: number; hostid: number }) {
  const [search, setSearch] = useState('');
  const [onlyOid, setOnlyOid] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const itemsQ = useQuery({
    queryKey: ['zbx-items', deviceId, search, onlyOid],
    queryFn: () => api.zbxItemsList(deviceId, { search: search || undefined, onlyOid })
  });
  const imp = useMutation({
    mutationFn: () => api.zbxItemsImport(deviceId),
    onSuccess: (d) => { setMsg(`OK: ${d.total} items (${d.created} novos, ${d.updated} atualizados)`); itemsQ.refetch(); },
    onError: (e: any) => setMsg(`erro: ${e.detail?.detail ?? e.detail?.error ?? e.message ?? 'falhou'}`)
  });
  return (
    <div className="mt-4 pt-4 border-t border-slate-700/40">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-sm font-semibold">Items Zabbix (hostid {hostid})</h3>
          <div className="text-xs text-slate-500">last import: {itemsQ.data?.items[0]?.lastImportedAt ? new Date(itemsQ.data.items[0].lastImportedAt).toLocaleString() : '—'}</div>
        </div>
        <button onClick={() => { setMsg(null); imp.mutate(); }} disabled={imp.isPending}
                className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 rounded text-sm disabled:opacity-50">
          {imp.isPending ? 'importando…' : 'Importar items do Zabbix'}
        </button>
      </div>
      {msg && <div className="text-xs text-slate-300 mb-2">{msg}</div>}
      <div className="flex items-center gap-2 mb-2">
        <input className={inp} placeholder="buscar por nome/key_…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <label className="text-xs text-slate-400 flex items-center gap-1">
          <input type="checkbox" checked={onlyOid} onChange={(e) => setOnlyOid(e.target.checked)} /> só OID (key_ com .1.3.6.)
        </label>
        <div className="text-xs text-slate-500 ml-auto">{itemsQ.data?.total ?? 0} item(ns)</div>
      </div>
      <div className="overflow-auto max-h-72 border border-slate-700/40 rounded">
        <table className="w-full text-xs">
          <thead className="bg-slate-800/60 sticky top-0">
            <tr><th className="px-2 py-1 text-left">name</th><th className="px-2 py-1 text-left">key_</th><th className="px-2 py-1">vt</th><th className="px-2 py-1">delay</th><th className="px-2 py-1">units</th></tr>
          </thead>
          <tbody>
            {(itemsQ.data?.items ?? []).slice(0, 500).map((it) => (
              <tr key={it.id} className="hover:bg-slate-800/30">
                <td className="px-2 py-0.5">{it.name}</td>
                <td className="px-2 py-0.5 font-mono text-[10px] text-slate-400 break-all">{it.key_}</td>
                <td className="px-2 py-0.5 text-center">{it.valueType ?? ''}</td>
                <td className="px-2 py-0.5 text-center">{it.delay ?? ''}</td>
                <td className="px-2 py-0.5 text-center">{it.units ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================== ACCESSES ==============================
// AccessesSection + AccessForm + AccessRow vivem em ./components/AccessesSection.
// Importados acima e reusados pelo DeviceForm (modal) e DeviceCredentialsTab.


// ============================== OPTICAL ==============================
const STATUS_COLORS: Record<string, string> = {
  ok: 'text-emerald-400 bg-emerald-500/10 ring-emerald-700/40',
  warning: 'text-amber-400 bg-amber-500/10 ring-amber-700/40',
  critical: 'text-rose-400 bg-rose-500/10 ring-rose-700/40',
  'no-data': 'text-slate-400 bg-slate-500/10 ring-slate-700/40'
};
function StatusBadge({ status }: { status: string }) {
  return <span className={`px-1.5 py-0.5 rounded text-[10px] ring-1 ${STATUS_COLORS[status] ?? STATUS_COLORS['no-data']}`}>{status}</span>;
}

function OpticalOverviewPage() {
  const q = useQuery({ queryKey: ['optical-overview'], queryFn: api.opticalOverview, refetchInterval: 60_000 });
  const [filter, setFilter] = useState('');
  const [statusF, setStatusF] = useState('');
  if (!q.data) return <Splash msg="carregando..." />;
  const { summary } = q.data;
  const rows = q.data.rows.filter((r) =>
    (!statusF || r.status === statusF) &&
    (!filter || `${r.hostname} ${r.ifaceName} ${r.transceiverModel} ${r.mediaType}`.toLowerCase().includes(filter.toLowerCase()))
  );
  return (
    <section className="space-y-4">
      <h1 className="text-xl font-semibold">Óptica / GBIC</h1>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card label="Total transceivers" value={summary.total} />
        <Card label="OK" value={summary.ok} accent="emerald" />
        <Card label="Warning" value={summary.warning} />
        <Card label="Critical" value={summary.critical} />
        <Card label="Sem dados" value={summary.noData} />
      </div>

      <Section title="Interfaces ópticas" sub={`${rows.length} de ${q.data.rows.length}`}>
        <div className="flex flex-wrap items-center gap-2 mb-3 -mt-1">
          <input placeholder="buscar host/iface/transceiver..." value={filter} onChange={(e) => setFilter(e.target.value)}
            className="bg-slate-950 ring-1 ring-slate-800 rounded px-3 py-1.5 text-xs flex-1 min-w-[14rem] max-w-md focus:outline-none focus:ring-emerald-600" />
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className="bg-slate-950 ring-1 ring-slate-800 rounded px-2 py-1.5 text-xs">
            <option value="">todos status</option>
            <option>ok</option><option>warning</option><option>critical</option><option>no-data</option>
          </select>
        </div>
        <table className="w-full text-xs">
          <thead className="text-slate-400"><tr className="border-b border-slate-800">
            <Th>Host</Th><Th>Iface</Th><Th>Transceiver</Th><Th>Media</Th><Th>RX dBm</Th><Th>TX dBm</Th><Th>Limiar RX</Th><Th>Status</Th>
          </tr></thead>
          <tbody>
            {rows.map((r) => {
              const rxNum = r.rxDbm;
              const lo = r.rxLowDbm, hi = r.rxHighDbm;
              const rxClass = rxNum == null ? 'text-slate-500' : (r.status === 'critical' ? 'text-rose-400' : r.status === 'warning' ? 'text-amber-400' : 'text-emerald-400');
              return (
                <tr key={r.ifaceId} className="border-b border-slate-800 hover:bg-slate-800/30 cursor-pointer" onClick={() => { window.location.hash = `#optical/${r.ifaceId}`; }}>
                  <Td className="font-mono text-slate-300 max-w-[20rem] truncate">{r.hostname}</Td>
                  <Td className="font-mono">{r.ifaceName}</Td>
                  <Td className="text-slate-400">
                    <div>{r.transceiverModel ?? '—'}</div>
                    <div className="text-[10px] text-slate-500">{r.transceiverVendor ?? ''}</div>
                  </Td>
                  <Td className="text-slate-400">{r.mediaType ?? '—'}</Td>
                  <Td className={`font-mono ${rxClass}`}>{rxNum != null ? rxNum.toFixed(2) : '—'}</Td>
                  <Td className="font-mono text-slate-300">{r.txDbm != null ? r.txDbm.toFixed(2) : '—'}</Td>
                  <Td className="text-[10px] text-slate-500">{lo != null && hi != null ? `[${lo.toFixed(1)} ↔ ${hi.toFixed(1)}]` : '—'}</Td>
                  <Td><StatusBadge status={r.status} /></Td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={8} className="text-center py-6 text-slate-500">sem resultados</td></tr>}
          </tbody>
        </table>
      </Section>
    </section>
  );
}

function OpticalIfaceDetail({ ifaceId }: { ifaceId: number }) {
  const q = useQuery({
    queryKey: ['optical-history', ifaceId],
    queryFn: () => api.opticalHistory(ifaceId, { bucket: '5m' }),
    refetchInterval: 30_000
  });
  if (!q.data) return <Splash msg="carregando..." />;
  const { iface, series } = q.data;
  return (
    <section className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => { window.location.hash = '#optical'; }} className="text-xs text-slate-400 hover:text-emerald-400">← voltar</button>
        <h1 className="text-xl font-semibold font-mono">{iface?.device?.hostname} · {iface?.name}</h1>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <Card label="Transceiver" value={0} />
        <div className="bg-slate-900 rounded ring-1 ring-slate-800 p-3 md:col-span-3 text-xs">
          <div className="text-slate-400">Modelo: <span className="text-slate-200">{iface?.transceiverModel ?? '—'}</span> · Vendor: <span className="text-slate-200">{iface?.transceiverVendor ?? '—'}</span></div>
          <div className="text-slate-400">Media: <span className="text-slate-200">{iface?.mediaType ?? '—'}</span> · λ: <span className="text-slate-200">{iface?.wavelengthNm ?? '—'}</span></div>
          <div className="text-slate-400">Threshold RX: <span className="text-slate-200">{iface?.rxLowDbm ?? '—'} ↔ {iface?.rxHighDbm ?? '—'} dBm</span> · TX: <span className="text-slate-200">{iface?.txLowDbm ?? '—'} ↔ {iface?.txHighDbm ?? '—'} dBm</span></div>
        </div>
      </div>
      <Section title="Histórico (5min bucket, últimas 24h)" sub={`${series.length} pontos`}>
        <OpticalChart series={series as any} rxLow={iface?.rxLowDbm ?? null} rxHigh={iface?.rxHighDbm ?? null} txLow={iface?.txLowDbm ?? null} txHigh={iface?.txHighDbm ?? null} />
      </Section>
    </section>
  );
}


// ============================== MAP ==============================
const LINK_COLOR: Record<string, string> = {
  UP: '#10b981',         // emerald
  DEGRADED: '#f59e0b',   // amber
  DOWN: '#ef4444',       // rose
  UNKNOWN: '#64748b'     // slate
};

// Marcadores de POP com tamanho responsivo ao zoom: bolinha quando o mapa está
// afastado (evita empilhamento dos ~96 POPs em AL/SE) → pin cheio ao aproximar.
function PopMarkers({ pops, onSelect }: { pops: Pop[]; onSelect: (p: Pop) => void }) {
  const map = useMap();
  const [zoom, setZoom] = useState<number>(() => map?.getZoom() ?? 7);
  useEffect(() => {
    if (!map) return;
    const update = () => setZoom(map.getZoom() ?? 7);
    update();
    const l = map.addListener('zoom_changed', update);
    return () => l.remove();
  }, [map]);

  // z < 10 → bolinha (5–9 px conforme afastamento); z ≥ 10 → pin com scale crescente
  const asDot = zoom < 10;
  const dotSize = zoom <= 7 ? 5 : zoom === 8 ? 6 : zoom === 9 ? 8 : 9;
  const pinScale = zoom >= 13 ? 1 : zoom >= 12 ? 0.85 : zoom >= 11 ? 0.7 : 0.6;

  return (
    <>
      {pops.map((p) => {
        const color = (p._count?.devices ?? 0) > 0 ? '#10b981' : '#64748b';
        return (
          <AdvancedMarker key={p.id} position={{ lat: p.latitude!, lng: p.longitude! }} onClick={() => onSelect(p)}>
            {asDot ? (
              <div
                style={{
                  width: dotSize,
                  height: dotSize,
                  borderRadius: '50%',
                  background: color,
                  border: '1.5px solid #0f172a',
                  boxShadow: '0 0 0 1px rgba(255,255,255,0.6)',
                  transform: 'translateY(50%)', // centra o ponto no lat/lng (AdvancedMarker ancora embaixo)
                  cursor: 'pointer',
                }}
              />
            ) : (
              <Pin background={color} borderColor="#0f172a" glyphColor="#0f172a" scale={pinScale} />
            )}
          </AdvancedMarker>
        );
      })}
    </>
  );
}

// Fullscreen API. Ler e sair aceitam também a forma prefixada (webkit), mas
// ENTRAR exige a forma padrão — ver fsCanEnter. Wrappers pra concentrar os
// `as any` (a lib do TS só tipa a API sem prefixo) num lugar só.
/**
 * iOS/iPadOS — incluindo o iPad que se declara "Macintosh" desde o iPadOS 13.
 * Um Mac de verdade não tem tela de toque (`maxTouchPoints` é 0 nele, e > 1 no
 * iPad), e é essa a única diferença confiável entre os dois. `navigator.platform`
 * está deprecado, mas o Safari não expõe `userAgentData`, então não há
 * substituto.
 */
function isAppleTouchDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const plat = (navigator as any).platform || '';
  return /iPad|iPhone|iPod/.test(ua)
    || (navigator.maxTouchPoints > 1 && /Mac/.test(plat));
}

/**
 * Onde é permitido entrar em fullscreen NATIVO: desktop e Android.
 *
 * iOS e iPadOS ficam de fora **por plataforma, não por capacidade**. Detectar a
 * API não resolve: o Safari 16.4 desprefixou a Fullscreen API no iPadOS e o
 * 17.2 a levou ao iPhone, então hoje `document.fullscreenEnabled` é `true` lá —
 * e a versão anterior desta função, que só checava isso, seguia entrando em
 * fullscreen no iPad. Só que no iOS o sistema exibe "deslize para baixo para
 * sair" e captura o gesto: o arrastar do mapa morre (relato do NOC, 2026-09-09,
 * duas vezes). Aviso e gesto são do sistema, não há API para suprimir.
 *
 * Como esta tela existe para navegar no mapa, esconder ~50px de barra do Safari
 * não paga o preço. Lá o pseudo-fullscreen por CSS (.nms-map-full) cobre a
 * viewport e preserva todos os gestos.
 */
function fsCanEnter(): boolean {
  if (typeof document === 'undefined') return false;
  if (isAppleTouchDevice()) return false;
  return Boolean((document as any).fullscreenEnabled)
    && typeof document.documentElement.requestFullscreen === 'function';
}
function fsElement(): Element | null {
  const d = document as any;
  return d.fullscreenElement ?? d.webkitFullscreenElement ?? null;
}
async function fsRequest(): Promise<void> {
  const el = document.documentElement as any;
  const fn = el.requestFullscreen ?? el.webkitRequestFullscreen;
  if (typeof fn !== 'function') return;
  // Safari antigo devolve undefined em vez de Promise; `await undefined` é
  // inofensivo, mas um `.catch()` direto quebraria.
  try { await fn.call(el); } catch { /* usuário negou, ou o browser recusou */ }
}
async function fsExit(): Promise<void> {
  const d = document as any;
  const fn = d.exitFullscreen ?? d.webkitExitFullscreen;
  if (typeof fn !== 'function') return;
  try { await fn.call(d); } catch { /* já saiu */ }
}
/** Registra o change nos dois nomes de evento; devolve o cleanup. */
function onFsChange(handler: () => void): () => void {
  document.addEventListener('fullscreenchange', handler);
  document.addEventListener('webkitfullscreenchange', handler);
  return () => {
    document.removeEventListener('fullscreenchange', handler);
    document.removeEventListener('webkitfullscreenchange', handler);
  };
}

/** Base do mapa da Topologia escolhida pelo usuário (lembrada neste navegador). */
type MapaBase = 'google' | 'osm';
const MAPA_BASE_KEY = 'nms.mapa.base';
function lerMapaBase(): MapaBase {
  try { return localStorage.getItem(MAPA_BASE_KEY) === 'osm' ? 'osm' : 'google'; } catch { return 'google'; }
}
const corDoEstado = (s: string) => (LINK_COLOR as Record<string, string>)[s] ?? '#64748b';

function MapPage({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  // Editar a topologia: ADMIN ou permissão TOPOLOGY_MAP_EDIT (grupo Topologia). A permissão não depende da role,
  // que o login pelo IXC recalcula a cada entrada pelo grupo do ERP.
  const meQ = useQuery({ queryKey: ['me'], queryFn: api.me, retry: false, staleTime: 60_000 });
  const canWrite = isAdmin || !!meQ.data?.permissions?.includes('TOPOLOGY_MAP_EDIT');
  const [base, setBaseState] = useState<MapaBase>(lerMapaBase);
  const setBase = (b: MapaBase) => { setBaseState(b); try { localStorage.setItem(MAPA_BASE_KEY, b); } catch { /* sem storage: só nesta aba */ } };
  const popsQ = useQuery({ queryKey: ['pops'], queryFn: () => api.pops() });
  const linksQ = useQuery({ queryKey: ['pop-links'], queryFn: () => api.popLinks(), refetchInterval: 30_000, refetchIntervalInBackground: true });
  const [selected, setSelected] = useState<Pop | null>(null);
  const [submap, setSubmap] = useState<Pop | null>(null);
  const [editingPop, setEditingPop] = useState<Pop | null>(null);
  const [editingLink, setEditingLink] = useState<PopLink | null>(null);
  const [creatingLink, setCreatingLink] = useState(false);
  const [fleetEnabled, setFleetEnabled] = useState(true);
  const [fleetFilter, setFleetFilter] = useState<'all' | 'online' | 'idle' | 'offline'>('all');
  // Tela cheia do mapa. Duas camadas, ambas necessárias:
  //  - Fullscreen API no <html>, onde existe (desktop e Android): é a ÚNICA
  //    forma de esconder o cromo do navegador e a barra do SO. CSS não faz isso
  //    — `position: fixed; inset: 0` cobre a viewport, que já exclui abas e
  //    barra de endereço. Foi por isso que, no computador, "não ficava cheia".
  //  - Classe .nms-map-full sempre: no iOS o WebKit não implementa
  //    requestFullscreen em elemento (e a Apple obriga todo browser do iPhone a
  //    usar WebKit, Chrome/Firefox iOS incluídos), então lá ela é o único
  //    recurso; e mesmo com fullscreen nativo é ela que faz o mapa ocupar tudo.
  // Pedimos o fullscreen no documentElement, e NÃO no container do mapa, de
  // propósito: assim todo o DOM continua descendente do fullscreenElement e os
  // modais (z-50) seguem aparecendo por cima do mapa (z-40) — o sumiço dos
  // modais era o motivo de o fullscreen nativo da Maps ter sido abandonado.
  const [mapFull, setMapFull] = useState(false);
  const fsApiSupported = fsCanEnter();
  function toggleMapFull() {
    const next = !mapFull;
    setMapFull(next);
    if (!fsApiSupported) return;
    if (next) void fsRequest();
    else if (fsElement()) void fsExit();
  }
  function closeMapFull() {
    setMapFull(false);
    if (fsApiSupported && fsElement()) void fsExit();
  }
  const pops = popsQ.data?.pops ?? [];
  const links = linksQ.data?.links ?? [];
  const withCoords = pops.filter((p) => p.latitude != null && p.longitude != null);
  const without = pops.filter((p) => p.latitude == null || p.longitude == null);
  const [fitTick, setFitTick] = useState(0);
  // Esc pra sair + trava o scroll do body enquanto o mapa cobre a tela (sem isso
  // o mobile "rola por baixo" do overlay fixo).
  useEffect(() => {
    if (!mapFull) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') closeMapFull(); };
    window.addEventListener('keydown', onEsc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onEsc);
      document.body.style.overflow = prev;
    };
  }, [mapFull]);
  // F11 e Esc saem do fullscreen sem passar pelo nosso botão; sem isto o overlay
  // CSS ficaria preso na tela depois que o browser já saiu.
  useEffect(() => {
    if (!fsApiSupported) return;
    return onFsChange(() => { if (!fsElement()) setMapFull(false); });
  }, [fsApiSupported]);
  const didInitialFitRef = useRef(false);
  useEffect(() => {
    if (didInitialFitRef.current) return;
    if (withCoords.length < 2) return;
    didInitialFitRef.current = true;
    setFitTick((t) => t + 1);
  }, [withCoords.length]);
  const fitPoints = withCoords.map((p) => ({ lat: p.latitude!, lng: p.longitude! }));
  const [focusTarget, setFocusTarget] = useState<{ lat: number; lng: number; zoom?: number } | null>(null);
  const [focusTick, setFocusTick] = useState(0);
  function doFocus(latlng: { lat: number; lng: number }, zoom?: number) {
    setFocusTarget({ ...latlng, zoom });
    setFocusTick((t) => t + 1);
  }
  // fitBounds no traçado de um enlace (click no alarme) — funciona também em fullscreen,
  // diferente do modal LinkEditor que renderiza fora do fullscreenElement.
  const [linkFit, setLinkFit] = useState<{ pts: Array<{ lat: number; lng: number }>; tick: number }>({ pts: [], tick: 0 });
  function fitLink(l: PopLink) {
    if (l.sourcePop.latitude == null || l.destPop.latitude == null) return;
    const pts = l.pathGeometry?.length
      ? l.pathGeometry.map((p) => ({ lat: p.lat, lng: p.lng }))
      : [{ lat: l.sourcePop.latitude!, lng: l.sourcePop.longitude! }, { lat: l.destPop.latitude!, lng: l.destPop.longitude! }];
    setLinkFit((s) => ({ pts, tick: s.tick + 1 }));
  }
  // Com o fullscreen no <html>, o modal continua descendente do fullscreenElement
  // e aparece por cima do mapa (z-50 > z-40). Antes era preciso sair da tela
  // cheia pra editar um enlace — com o fullscreen num descendente do mapa, o
  // modal simplesmente não renderizava.
  function openLinkEditor(l: PopLink) {
    setEditingLink(l);
  }

  // links visíveis (precisam de ambos POPs com coords). SEM dedup: cada enlace vira sua própria linha.
  const visibleLinks = links.filter((l) => l.sourcePop.latitude != null && l.destPop.latitude != null);
  // Enlaces paralelos RETOS (mesmo par de POPs, sem rota desenhada) recebem um fator de arco
  // simétrico pra não se sobreporem (ex.: anel A↔B porta1/porta2). Quem tem pathGeometry própria
  // já está separado por rota e fica reto. Mapa linkId → fator (0 = reto).
  const bowFactors = (() => {
    const groups = new Map<string, PopLink[]>();
    for (const l of visibleLinks) {
      if (l.pathGeometry?.length) continue; // rota própria já separa
      const k = [l.sourcePop.id, l.destPop.id].sort((a, b) => a - b).join('-');
      let arr = groups.get(k);
      if (!arr) { arr = []; groups.set(k, arr); }
      arr.push(l);
    }
    const m = new Map<number, number>();
    for (const arr of groups.values()) {
      if (arr.length < 2) continue; // sozinho = reto
      arr.sort((a, b) => a.id - b.id);
      const n = arr.length;
      arr.forEach((l, i) => m.set(l.id, i - (n - 1) / 2));
    }
    return m;
  })();

  // OpenStreetMap: mesmas linhas do Google (traçado próprio, arco dos paralelos ou reta)
  const osmPops = useMemo(() => (popsQ.data?.pops ?? []).filter((p) => p.latitude != null && p.longitude != null), [popsQ.data]);
  const osmLinks = useMemo(() => (linksQ.data?.links ?? [])
    .filter((l) => l.sourcePop.latitude != null && l.destPop.latitude != null)
    .map((l) => {
      const s = { lat: l.sourcePop.latitude!, lng: l.sourcePop.longitude! }, e = { lat: l.destPop.latitude!, lng: l.destPop.longitude! };
      const bow = bowFactors.get(l.id) ?? 0;
      return { link: l, path: l.pathGeometry?.length ? l.pathGeometry.map((p) => ({ lat: p.lat, lng: p.lng })) : (bow ? bowedPath(s, e, bow) : [s, e]) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [linksQ.data]);

  if (!GMAPS_KEY) {
    return <div className="bg-rose-500/10 ring-1 ring-rose-700/40 rounded p-4 text-rose-300">
      VITE_GOOGLE_MAPS_API_KEY ausente no build.
    </div>;
  }

  return (
    <APIProvider apiKey={GMAPS_KEY} libraries={['places', 'routes', 'geometry']}>
      <section className="space-y-3">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold">Mapa</h1>
          <span className="text-xs text-slate-400">{withCoords.length}/{pops.length} POPs · {visibleLinks.length}/{links.length} enlaces visíveis</span>
          <div className="ml-auto inline-flex rounded overflow-hidden ring-1 ring-slate-700 text-xs" title="Base do mapa (fica lembrada neste navegador)">
            {(['google', 'osm'] as const).map((b) => (
              <button key={b} onClick={() => setBase(b)}
                className={`px-2 py-1 ${base === b ? 'bg-sky-700 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}>
                {b === 'google' ? 'Google Maps' : 'OpenStreetMap'}
              </button>
            ))}
          </div>
          {canWrite && <button onClick={() => setCreatingLink(true)}
            className="px-2 py-1 text-xs rounded bg-emerald-600 hover:bg-emerald-500">+ Novo enlace</button>}
          {isAdmin && <button onClick={async () => { if (!confirm('Iniciar coleta LLDP agora? Pode levar alguns minutos.')) return; const r = await api.topologyDiscoverLldp(); alert(`LLDP: ${r.neighbors} vizinhos coletados em ${r.ok}/${r.devices} devices (${r.rawUpserts ?? 0} novos/atualizados) — revise e converta em Topologia → LLDP`); qc.invalidateQueries({ queryKey: ['lldp-neighbors'] }); }}
            className="px-3 py-1.5 text-xs bg-amber-700 hover:bg-amber-600 rounded text-white"
            title="Roda discover-lldp on-demand">⚡ Coletar LLDP</button>}
          <button onClick={() => setFitTick((t) => t + 1)}
            className="px-2 py-1 text-xs rounded bg-slate-700 hover:bg-slate-600"
            title="Encaixar nos POPs mais distantes (N/S/L/O)">↔ Encaixar</button>
        </div>

        {/* `relative` só no modo normal: junto com .nms-map-full ele CONFLITA na
            propriedade `position` — mesma especificidade (1 classe), e o Tailwind
            Play CDN injeta o CSS dele em runtime, depois do <style> do index.html,
            então `.relative` vencia e o mapa continuava no fluxo (esticava na
            vertical pelo height, mas mantinha a largura do container). Em tela
            cheia o próprio `position: fixed` já serve de referência pro botão
            `absolute` de sair. */}
        {base === 'osm' && (
          // isolate: os panes do Leaflet (z 400–1000) e o painel z-[1000] ficam presos aqui dentro;
          // sem isso cobriam o modal do editor de enlace (z-50) ao clicar numa linha
          <div className={`bg-slate-900 ring-1 ring-slate-800 overflow-hidden isolate ${mapFull ? 'nms-map-full' : 'nms-map rounded relative'}`}>
            <MapaOsm pops={osmPops} links={osmLinks} corDoEstado={corDoEstado}
              onSelectPop={setSelected} onClickLink={openLinkEditor}
              encaixe={{ pts: fitPoints, chave: `m${fitTick}` }} encaixeEnlace={{ pts: linkFit.pts, chave: `l${linkFit.tick}` }}
              foco={{ alvo: focusTarget, chave: focusTick }} />
            {/* Painel lateral e popup do POP por cima do Leaflet (panes até z 1000) */}
            <div className="absolute right-4 top-14 z-[1000] max-h-[calc(100%-5rem)] overflow-auto">
              <MapSidePanel
                pops={pops}
                visibleLinks={visibleLinks}
                onFocus={doFocus}
                onEditLink={openLinkEditor}
                onFitLink={fitLink}
                onResetView={() => setFitTick((t) => t + 1)}
                fleetEnabled={false}
                setFleetEnabled={() => {}}
                fleetFilter={fleetFilter}
                setFleetFilter={setFleetFilter}
              />
            </div>
            {selected && (
              <div className="absolute left-14 top-3 z-[1000] rounded bg-white shadow ring-1 ring-slate-300 p-3" style={{ color: '#0f172a', minWidth: 220 }}>
                <div className="flex justify-between gap-2">
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{selected.uf ? `[${selected.uf}] ` : ''}{selected.name}</div>
                  <button onClick={() => setSelected(null)} className="text-slate-500 hover:text-slate-900" aria-label="Fechar">✕</button>
                </div>
                <div style={{ fontSize: 11, color: '#475569' }}>{selected.city ?? '—'}</div>
                <div style={{ fontSize: 11, marginTop: 6 }}><code style={{ background: '#e2e8f0', padding: '1px 4px', borderRadius: 3 }}>{selected.code}</code></div>
                <div style={{ fontSize: 11, marginTop: 4 }}>{selected._count?.devices ?? 0} devices</div>
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                  <button onClick={() => { setSubmap(selected); setSelected(null); }} style={{ fontSize: 11, color: '#0284c7', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>abrir submap interno →</button>
                  {canWrite && <button onClick={() => { setEditingPop(selected); setSelected(null); }} style={{ fontSize: 11, color: '#0284c7', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>editar POP →</button>}
                </div>
              </div>
            )}
            <button
              onClick={toggleMapFull}
              className="absolute top-2 right-2 z-[1000] px-2.5 py-1.5 rounded bg-white/95 hover:bg-white text-slate-800 text-sm shadow ring-1 ring-slate-300"
              title={mapFull ? 'Sair da tela cheia (Esc)' : 'Tela cheia'}
              aria-label={mapFull ? 'Sair da tela cheia' : 'Tela cheia'}
            >{mapFull ? '✕' : '⛶'}</button>
          </div>
        )}

        {base === 'google' && <div className={`bg-slate-900 ring-1 ring-slate-800 overflow-hidden ${mapFull ? 'nms-map-full' : 'nms-map rounded relative'}`}>
          <GMap             mapId={GMAPS_MAP_ID}
            defaultCenter={SC_CENTER}
            defaultZoom={7}
            gestureHandling="greedy"
            colorScheme="LIGHT"
            fullscreenControl={false}
            style={{ width: '100%', height: '100%' }}
          >
            <FitMapToPath points={fitPoints} runKey={`map:${fitTick}`} />
            <FitMapToPath points={linkFit.pts} runKey={`linkfit:${linkFit.tick}`} />
            <MapFocus target={focusTarget} runKey={focusTick} />
            <MapFloatingPanel defaultPos={{ right: 16, top: 60 }}>
              <MapSidePanel
                pops={pops}
                visibleLinks={visibleLinks}
                onFocus={doFocus}
                onEditLink={openLinkEditor}
                onFitLink={fitLink}
                onResetView={() => setFitTick((t) => t + 1)}
                fleetEnabled={fleetEnabled}
                setFleetEnabled={setFleetEnabled}
                fleetFilter={fleetFilter}
                setFleetFilter={setFleetFilter}
              />
            </MapFloatingPanel>
            {visibleLinks.map((l) => (
              <LinkPolyline key={l.id} link={l} bowFactor={bowFactors.get(l.id) ?? 0} onClick={() => openLinkEditor(l)} />
            ))}
            <FleetLayer enabled={fleetEnabled} filter={fleetFilter} />
            <PopMarkers pops={withCoords} onSelect={setSelected} />
            {selected && selected.latitude != null && selected.longitude != null && (
              <InfoWindow position={{ lat: selected.latitude, lng: selected.longitude }} onCloseClick={() => setSelected(null)}>
                <div style={{ color: '#0f172a', minWidth: 220 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{selected.uf ? `[${selected.uf}] ` : ''}{selected.name}</div>
                  <div style={{ fontSize: 11, color: '#475569' }}>{selected.city ?? '—'}</div>
                  <div style={{ fontSize: 11, marginTop: 6 }}><code style={{ background: '#e2e8f0', padding: '1px 4px', borderRadius: 3 }}>{selected.code}</code></div>
                  <div style={{ fontSize: 11, marginTop: 4 }}>{selected._count?.devices ?? 0} devices</div>
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                    <button onClick={() => { setSubmap(selected); setSelected(null); }} style={{ fontSize: 11, color: '#0284c7', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>abrir submap interno →</button>
                    {canWrite && <button onClick={() => { setEditingPop(selected); setSelected(null); }} style={{ fontSize: 11, color: '#0284c7', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>editar POP →</button>}
                  </div>
                </div>
              </InfoWindow>
            )}
          </GMap>
          {/* Substitui o controle nativo da Maps (desligado em fullscreenControl={false}),
              que não existe no iOS e some justamente no celular. Fica fora do <GMap> pra
              ser um irmão posicionado no container, não um overlay do mapa. */}
          <button
            onClick={toggleMapFull}
            className="absolute top-2 right-2 z-10 px-2.5 py-1.5 rounded bg-white/95 hover:bg-white text-slate-800 text-sm shadow ring-1 ring-slate-300"
            title={mapFull ? 'Sair da tela cheia (Esc)' : 'Tela cheia'}
            aria-label={mapFull ? 'Sair da tela cheia' : 'Tela cheia'}
          >{mapFull ? '✕' : '⛶'}</button>
        </div>}

        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span>Estados:</span>
          {(['UP', 'DEGRADED', 'DOWN', 'UNKNOWN'] as const).map((s) => (
            <span key={s} className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-0.5" style={{ background: LINK_COLOR[s] }}></span>{s}
            </span>
          ))}
          <span className="ml-auto text-slate-500">{base === 'osm' ? 'OpenStreetMap: frota e desenho do trajeto só no Google · ' : ''}Clique numa linha pra editar trajeto (admin ou grupo Topologia)</span>
        </div>

        {without.length > 0 && (
          <Section title="POPs sem coordenadas" sub={`${without.length} aguardando lat/lng`}>
            <table className="w-full text-xs">
              <thead className="text-slate-400"><tr className="border-b border-slate-800">
                <Th>UF</Th><Th>Nome</Th><Th>Cidade</Th><Th>Devices</Th><Th className="w-32">Ações</Th>
              </tr></thead>
              <tbody>
                {without.map((p) => (
                  <tr key={p.id} className="border-b border-slate-800 hover:bg-slate-800/30">
                    <Td>{p.uf ? <code className="text-emerald-400">{p.uf}</code> : '—'}</Td>
                    <Td>{p.name}</Td>
                    <Td className="text-slate-400">{p.city ?? '—'}</Td>
                    <Td>{p._count?.devices ?? 0}</Td>
                    <Td><a href="#pops" className="text-xs text-emerald-400 hover:underline">editar POP</a></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}
      </section>

      {editingLink && <LinkEditor link={editingLink} isAdmin={canWrite} onClose={() => setEditingLink(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['pop-links'] }); setEditingLink(null); }} />}
      {submap && <PopSubmap pop={submap} onClose={() => setSubmap(null)} canWrite={canWrite} />}
      {editingPop && <PopForm pop={editingPop} onClose={() => { setEditingPop(null); qc.invalidateQueries({ queryKey: ['pops'] }); }} />}
      {creatingLink && <NewLinkForm pops={withCoords} onClose={() => setCreatingLink(false)} onCreated={(link) => { qc.invalidateQueries({ queryKey: ['pop-links'] }); setCreatingLink(false); setEditingLink(link); }} />}
    </APIProvider>
  );
}

function NewLinkForm({ pops, onClose, onCreated }: { pops: Pop[]; onClose: () => void; onCreated: (link: PopLink) => void }) {
  const [sourcePopId, setSourcePopId] = useState<number>(pops[0]?.id ?? 0);
  const [destPopId, setDestPopId] = useState<number>(pops[1]?.id ?? 0);
  const [sourceDeviceId, setSourceDeviceId] = useState<number | undefined>();
  const [destDeviceId, setDestDeviceId] = useState<number | undefined>();
  const [sourceIfaceName, setSourceIfaceName] = useState<string>('');
  const [destIfaceName, setDestIfaceName] = useState<string>('');
  const [type, setType] = useState<'IBGP'|'EBGP'|'LLDP'|'STATIC'>('STATIC');
  const [err, setErr] = useState<string | null>(null);

  const devicesAQ = useQuery({ queryKey: ['devices-of-pop', sourcePopId], queryFn: () => api.devices({ popId: sourcePopId, take: 100 }), enabled: !!sourcePopId });
  const devicesBQ = useQuery({ queryKey: ['devices-of-pop', destPopId], queryFn: () => api.devices({ popId: destPopId, take: 100 }), enabled: !!destPopId });
  const ifaceAQ = useQuery({ queryKey: ['zbx-ifaces', sourceDeviceId], queryFn: () => api.zbxInterfaces(sourceDeviceId!), enabled: !!sourceDeviceId });
  const ifaceBQ = useQuery({ queryKey: ['zbx-ifaces', destDeviceId], queryFn: () => api.zbxInterfaces(destDeviceId!), enabled: !!destDeviceId });

  useEffect(() => { setSourceDeviceId(undefined); setSourceIfaceName(''); }, [sourcePopId]);
  useEffect(() => { setDestDeviceId(undefined); setDestIfaceName(''); }, [destPopId]);
  useEffect(() => setSourceIfaceName(''), [sourceDeviceId]);
  useEffect(() => setDestIfaceName(''), [destDeviceId]);

  const create = useMutation({
    mutationFn: () => api.popLinkCreate({ sourcePopId, destPopId, type, sourceDeviceId, destDeviceId, sourceIfaceName: sourceIfaceName || undefined, destIfaceName: destIfaceName || undefined }),
    onSuccess: (d) => onCreated(d.link),
    onError: (e: any) => setErr(e.detail?.error === 'duplicate_link' ? 'já existe enlace deste tipo entre estes equipamentos nas mesmas portas' : (e.detail?.detail ?? e.message ?? 'erro'))
  });

  const devicesA = devicesAQ.data?.devices ?? [];
  const devicesB = devicesBQ.data?.devices ?? [];
  const ifacesA = ifaceAQ.data?.interfaces ?? [];
  const ifacesB = ifaceBQ.data?.interfaces ?? [];

  return (
    <Modal title="Novo enlace inter-POP" onClose={onClose} wide>
      <div className="grid md:grid-cols-2 gap-3">
        <div className="space-y-2 ring-1 ring-slate-800 rounded p-3">
          <div className="text-xs font-semibold text-emerald-400 mb-1">▶ Ponta A (origem)</div>
          <FormRow label="POP">
            <Combobox
              options={pops.map((p: any) => ({
                id: p.id,
                label: `${p.uf ? `[${p.uf}] ` : ''}${p.name}`,
                aliases: [p.code, p.city, p.uf].filter(Boolean).join(' ')
              }))}
              value={sourcePopId || null}
              onChange={(id) => id != null && setSourcePopId(id)}
              placeholder="POP origem (digite pra buscar)"
            />
          </FormRow>
          <FormRow label={`Equipamento (${devicesA.length})`}>
            <Combobox
              options={devicesA.map((d: any) => ({
                id: d.id,
                label: d.hostname,
                aliases: [d.mgmtIp, d.vendor?.code, d.model?.code].filter(Boolean).join(' ')
              }))}
              value={sourceDeviceId ?? null}
              onChange={(id) => setSourceDeviceId(id ?? undefined)}
              placeholder="— auto (1º do POP) — (digite pra buscar)"
            />
          </FormRow>
          <FormRow label={`Interface (${ifacesA.length})`}>
            <select className={inp} value={sourceIfaceName} onChange={(e) => setSourceIfaceName(e.target.value)} disabled={!sourceDeviceId}>
              <option value="">— sem iface —</option>
              {ifacesA.map((i) => <option key={i.name} value={i.name}>{i.name}{i.description ? ` · ${i.description}` : ''}{i.source === 'zabbix' ? '' : i.source === 'local' ? ' [snmp]' : ''}</option>)}
            </select>
          </FormRow>
          {sourceDeviceId && ifacesA.length === 0 && (
            <IfaceDiscoverBtn deviceId={sourceDeviceId} onDone={() => ifaceAQ.refetch()} />
          )}
        </div>
        <div className="space-y-2 ring-1 ring-slate-800 rounded p-3">
          <div className="text-xs font-semibold text-rose-400 mb-1">◀ Ponta B (destino)</div>
          <FormRow label="POP">
            <Combobox
              options={pops.filter((p: any) => p.id !== sourcePopId).map((p: any) => ({
                id: p.id,
                label: `${p.uf ? `[${p.uf}] ` : ''}${p.name}`,
                aliases: [p.code, p.city, p.uf].filter(Boolean).join(' ')
              }))}
              value={destPopId || null}
              onChange={(id) => id != null && setDestPopId(id)}
              placeholder="POP destino (digite pra buscar)"
            />
          </FormRow>
          <FormRow label={`Equipamento (${devicesB.length})`}>
            <Combobox
              options={devicesB.map((d: any) => ({
                id: d.id,
                label: d.hostname,
                aliases: [d.mgmtIp, d.vendor?.code, d.model?.code].filter(Boolean).join(' ')
              }))}
              value={destDeviceId ?? null}
              onChange={(id) => setDestDeviceId(id ?? undefined)}
              placeholder="— auto (1º do POP) — (digite pra buscar)"
            />
          </FormRow>
          <FormRow label={`Interface (${ifacesB.length})`}>
            <select className={inp} value={destIfaceName} onChange={(e) => setDestIfaceName(e.target.value)} disabled={!destDeviceId}>
              <option value="">— sem iface —</option>
              {ifacesB.map((i) => <option key={i.name} value={i.name}>{i.name}{i.description ? ` · ${i.description}` : ''}{i.source === 'zabbix' ? '' : i.source === 'local' ? ' [snmp]' : ''}</option>)}
            </select>
          </FormRow>
          {destDeviceId && ifacesB.length === 0 && (
            <IfaceDiscoverBtn deviceId={destDeviceId} onDone={() => ifaceBQ.refetch()} />
          )}
        </div>
      </div>
      <FormRow label="Tipo do enlace">
        <select className={inp} value={type} onChange={(e) => setType(e.target.value as any)}>
          <option value="STATIC">STATIC — enlace estático/manual</option>
          <option value="IBGP">IBGP — sessão iBGP</option>
          <option value="EBGP">EBGP — sessão eBGP</option>
          <option value="LLDP">LLDP — vizinhança L2 (LLDP)</option>
        </select>
      </FormRow>
      <div className="text-xs text-slate-500">Após criar, abre o editor de trajeto (auto-rotear via rodovias / waypoints manuais / linha reta).</div>
      {err && <div className="text-rose-400 text-sm">{err}</div>}
      <FormActions onClose={onClose} onSave={() => { setErr(null); create.mutate(); }} pending={create.isPending} />
    </Modal>
  );
}

function stateRank(s: string): number {
  return ({ UP: 1, DEGRADED: 2, DOWN: 3, UNKNOWN: 0 } as any)[s] ?? 0;
}

// Gera um caminho arqueado (Bézier quadrático) entre s e e, deslocado lateralmente por `factor`.
// Usado pra separar visualmente enlaces paralelos retos (sem rota própria) no mesmo par de POPs.
function bowedPath(s: { lat: number; lng: number }, e: { lat: number; lng: number }, factor: number): Array<{ lat: number; lng: number }> {
  const midLat = (s.lat + e.lat) / 2;
  const cosLat = Math.cos((midLat * Math.PI) / 180) || 1;
  let dx = (e.lng - s.lng) * cosLat;
  let dy = e.lat - s.lat;
  const chord = Math.hypot(dx, dy) || 1e-6;
  dx /= chord; dy /= chord;
  const px = -dy, py = dx; // perpendicular unitária (espaço métrico aproximado)
  const amp = factor * Math.max(chord * 0.12, 0.01); // offset do ponto de controle (graus, espaço métrico)
  const ctrl = {
    lat: midLat + py * amp,
    lng: (s.lng + e.lng) / 2 + (px * amp) / cosLat,
  };
  const STEPS = 24;
  const pts: Array<{ lat: number; lng: number }> = [];
  for (let i = 0; i <= STEPS; i++) {
    const t = i / STEPS, u = 1 - t;
    pts.push({
      lat: u * u * s.lat + 2 * u * t * ctrl.lat + t * t * e.lat,
      lng: u * u * s.lng + 2 * u * t * ctrl.lng + t * t * e.lng,
    });
  }
  return pts;
}

function LinkPolyline({ link, onClick, bowFactor = 0 }: { link: PopLink; onClick: () => void; bowFactor?: number }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    const s = { lat: link.sourcePop.latitude!, lng: link.sourcePop.longitude! };
    const e = { lat: link.destPop.latitude!, lng: link.destPop.longitude! };
    const path = link.pathGeometry?.length
      ? link.pathGeometry.map((p) => ({ lat: p.lat, lng: p.lng }))
      : (bowFactor ? bowedPath(s, e, bowFactor) : [s, e]);
    const pending = link.approved === false;
    const poly = new (window as any).google.maps.Polyline({
      path,
      strokeColor: LINK_COLOR[link.state] ?? '#64748b',
      strokeOpacity: pending ? 0 : 0.85,
      strokeWeight: 3,
      clickable: true,
      icons: pending ? [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3 }, offset: '0', repeat: '12px' }] : undefined,
      map
    });
    const listener = poly.addListener('click', onClick);
    // Enlaces DOWN pulsam (opacidade alternando) pra puxar o olho em telão de NOC.
    let pulseTimer: number | undefined;
    if (link.state === 'DOWN' && !pending) {
      let dim = false;
      pulseTimer = window.setInterval(() => {
        dim = !dim;
        poly.setOptions({ strokeOpacity: dim ? 0.25 : 0.95, strokeWeight: dim ? 3 : 4 });
      }, 600);
    }
    return () => { if (pulseTimer) window.clearInterval(pulseTimer); listener.remove(); poly.setMap(null); };
  }, [map, link.id, link.state, bowFactor, JSON.stringify(link.pathGeometry)]);
  return null;
}

// ---------- Helpers de distância (haversine) usados pelo LinkEditor ----------
function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
function pathLengthKm(pts: Array<{ lat: number; lng: number }>): number {
  let total = 0;
  for (let i = 1; i < pts.length; i++) total += haversineKm(pts[i - 1], pts[i]);
  return total;
}
function PopEndpointMarker({ color, arrow, label }: { color: string; arrow: string; label: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none' }}>
      <div style={{
        width: 18, height: 18, borderRadius: '50%',
        background: color, border: '3px solid #0f172a',
        boxShadow: '0 2px 4px rgba(0,0,0,0.5)'
      }} />
      <div style={{
        marginTop: 3, padding: '2px 6px', borderRadius: 4,
        background: 'rgba(15,23,42,0.92)', color,
        fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
        border: `1px solid ${color}`, fontFamily: 'system-ui'
      }}>{arrow} {label}</div>
    </div>
  );
}
function MapFloatingPanel({ children, defaultPos }: {
  children: React.ReactNode;
  defaultPos?: { right?: number; top?: number; left?: number; bottom?: number };
}) {
  const map = useMap();
  const containerRef = useRef<HTMLDivElement | null>(null);
  if (!containerRef.current) {
    const d = document.createElement('div');
    d.style.position = 'absolute';
    d.style.zIndex = '100';
    if (defaultPos?.right != null) d.style.right = `${defaultPos.right}px`;
    if (defaultPos?.top != null) d.style.top = `${defaultPos.top}px`;
    if (defaultPos?.left != null) d.style.left = `${defaultPos.left}px`;
    if (defaultPos?.bottom != null) d.style.bottom = `${defaultPos.bottom}px`;
    containerRef.current = d;
  }
  useEffect(() => {
    if (!map) return;
    const div = containerRef.current!;
    const mapDiv = map.getDiv() as HTMLElement;
    const reattach = () => {
      // Em fullscreen só descendentes do fullscreenElement renderizam.
      // A Maps faz fullscreen num descendente do mapDiv (não no próprio mapDiv),
      // então sempre que houver fullscreenElement anexamos lá; senão, no mapDiv.
      const fe = fsElement() as HTMLElement | null;
      // Quando o fullscreen é do <html> (nosso botão de tela cheia), o mapDiv já
      // é descendente dele e o painel está no lugar certo — reanexar no <html>
      // arrancaria o painel de dentro do mapa. Só quando o elemento em fullscreen
      // NÃO contém o mapDiv (fullscreen nativo da Maps, feito num descendente
      // dele) é que o painel precisa ser movido pra lá.
      const target = fe && !fe.contains(mapDiv) ? fe : mapDiv;
      if (div.parentElement !== target) {
        try { div.parentElement?.removeChild(div); } catch {}
        target.appendChild(div);
      }
    };
    reattach();
    const offFs = onFsChange(reattach);
    return () => {
      offFs();
      try { div.parentElement?.removeChild(div); } catch {}
    };
  }, [map]);
  useEffect(() => {
    const div = containerRef.current!;
    const THRESHOLD = 4;
    let armed = false;     // pointerdown num handle válido — drag pode iniciar
    let dragging = false;  // movimento ultrapassou threshold
    let startX = 0, startY = 0, startLeft = 0, startTop = 0;
    let pointerId = -1;
    const onDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t?.closest('[data-drag-handle]')) return;
      if (t?.closest('[data-no-drag]')) return;
      const parent = div.parentElement as HTMLElement;
      if (!parent) return;
      armed = true;
      dragging = false;
      pointerId = e.pointerId;
      const r = div.getBoundingClientRect();
      const pr = parent.getBoundingClientRect();
      startX = e.clientX; startY = e.clientY;
      startLeft = r.left - pr.left;
      startTop = r.top - pr.top;
      // não preventDefault aqui — deixa o click rolar se for um click curto
    };
    const onMove = (e: PointerEvent) => {
      if (!armed) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!dragging) {
        if (Math.abs(dx) < THRESHOLD && Math.abs(dy) < THRESHOLD) return;
        dragging = true;
        try { div.setPointerCapture(pointerId); } catch {}
        div.style.left = `${startLeft}px`;
        div.style.top = `${startTop}px`;
        div.style.right = 'auto';
        div.style.bottom = 'auto';
      }
      const parent = div.parentElement as HTMLElement;
      const pw = parent.clientWidth, ph = parent.clientHeight;
      const dw = div.offsetWidth, dh = div.offsetHeight;
      const nl = Math.max(0, Math.min(pw - dw, startLeft + dx));
      const nt = Math.max(0, Math.min(ph - dh, startTop + dy));
      div.style.left = `${nl}px`;
      div.style.top = `${nt}px`;
      e.preventDefault();
    };
    const onUp = (e: PointerEvent) => {
      if (!armed) return;
      armed = false;
      if (dragging) {
        dragging = false;
        try { div.releasePointerCapture(e.pointerId); } catch {}
      }
    };
    div.addEventListener('pointerdown', onDown);
    div.addEventListener('pointermove', onMove);
    div.addEventListener('pointerup', onUp);
    div.addEventListener('pointercancel', onUp);
    return () => {
      div.removeEventListener('pointerdown', onDown);
      div.removeEventListener('pointermove', onMove);
      div.removeEventListener('pointerup', onUp);
      div.removeEventListener('pointercancel', onUp);
    };
  }, []);
  return createPortal(children, containerRef.current);
}
function MapFocus({ target, runKey }: { target: { lat: number; lng: number; zoom?: number } | null; runKey: string | number }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !target) return;
    map.panTo({ lat: target.lat, lng: target.lng });
    if (target.zoom != null) map.setZoom(target.zoom);
  }, [map, runKey]);
  return null;
}
function FitMapToPath({ points, runKey }: { points: Array<{ lat: number; lng: number }>; runKey: string | number }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google?.maps || points.length < 2) return;
    const bounds = new (window as any).google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));
    const div = (map as any).getDiv?.() as HTMLElement | undefined;
    const w = div?.offsetWidth ?? 600;
    const h = div?.offsetHeight ?? 420;
    const pad = {
      top: Math.max(40, Math.round(h * 0.1)),
      right: Math.max(40, Math.round(w * 0.1)),
      bottom: Math.max(40, Math.round(h * 0.1)),
      left: Math.max(40, Math.round(w * 0.1)),
    };
    map.fitBounds(bounds, pad);
  }, [map, runKey]);
  return null;
}

// ---------- LinkEditor: modal com mapa + editor de waypoints + "auto-rotear" ----------
function LinkEditor({ link, onClose, onSaved, isAdmin = false }: { link: PopLink; onClose: () => void; onSaved: () => void; isAdmin?: boolean }) {
  const initial = link.pathGeometry?.length ? link.pathGeometry : [
    { lat: link.sourcePop.latitude!, lng: link.sourcePop.longitude! },
    { lat: link.destPop.latitude!, lng: link.destPop.longitude! }
  ];
  const [pts, setPts] = useState<Array<{lat:number;lng:number}>>(initial);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fitTick, setFitTick] = useState(0);
  // Corrigir pontas (host + interface) — ADMIN. device tem que estar no POP da ponta.
  const [editEnds, setEditEnds] = useState(false);
  const [srcDev, setSrcDev] = useState<number>(link.sourceDevice.id);
  const [dstDev, setDstDev] = useState<number>(link.destDevice.id);
  const [srcIf, setSrcIf] = useState<string>(link.sourceIface?.name ?? '');
  const [dstIf, setDstIf] = useState<string>(link.destIface?.name ?? '');
  const [endMsg, setEndMsg] = useState<string | null>(null);
  const srcDevsQ = useQuery({ queryKey: ['devices', 'pop', link.sourcePop.id], queryFn: () => api.devices({ popId: link.sourcePop.id, take: 200 }), enabled: editEnds });
  const dstDevsQ = useQuery({ queryKey: ['devices', 'pop', link.destPop.id], queryFn: () => api.devices({ popId: link.destPop.id, take: 200 }), enabled: editEnds });
  const srcIfQ = useQuery({ queryKey: ['zbx-ifaces', srcDev], queryFn: () => api.zbxInterfaces(srcDev), enabled: editEnds && !!srcDev });
  const dstIfQ = useQuery({ queryKey: ['zbx-ifaces', dstDev], queryFn: () => api.zbxInterfaces(dstDev), enabled: editEnds && !!dstDev });
  const saveEnds = useMutation({
    mutationFn: () => api.popLinkUpdate(link.id, { sourceDeviceId: srcDev, destDeviceId: dstDev, sourceIfaceName: srcIf || null, destIfaceName: dstIf || null }),
    onSuccess: onSaved,
    onError: (e: any) => setEndMsg(
      e?.detail?.error === 'duplicate_link' ? 'já existe enlace entre esses devices'
      : e?.detail?.error === 'device_not_in_pop' ? 'o host escolhido não está nesse POP'
      : (e?.detail?.error ?? e?.message ?? 'erro'))
  });
  // F12 — Operação (ownership + provider)
  const linkAny = link as any;
  const [ownership, setOwnership] = useState<'OWN' | 'SWAP' | 'TRANSPORT'>(linkAny.ownership ?? 'OWN');
  const [providerId, setProviderId] = useState<number | null>(linkAny.providerId ?? null);
  const [circuitId, setCircuitId] = useState<string>(linkAny.providerCircuitId ?? '');
  const [costReais, setCostReais] = useState<string>(linkAny.monthlyCostCents != null ? (linkAny.monthlyCostCents / 100).toFixed(2) : '');
  const opProvidersQ = useQuery({
    queryKey: ['providers', 'swap-transport'],
    queryFn: async () => {
      const [swap, transp] = await Promise.all([
        api.providers({ kind: 'SWAP_FIBER', enabled: true }),
        api.providers({ kind: 'TRANSPORT', enabled: true })
      ]);
      return { providers: [...swap.providers, ...transp.providers] };
    }
  });
  const save = useMutation({
    mutationFn: (body: any) => api.popLinkUpdate(link.id, body),
    onSuccess: onSaved,
    onError: (e: any) => setMsg(e?.message ?? 'erro')
  });
  const del = useMutation({
    mutationFn: () => api.popLinkDelete(link.id),
    onSuccess: onSaved,
    onError: (e: any) => setMsg(`erro ao excluir: ${e?.message ?? 'falhou'}`)
  });

  async function autoRoute() {
    setBusy(true); setMsg(null);
    try {
      if (!(window as any).google?.maps) { setMsg('Maps ainda carregando'); return; }
      const directions = new (window as any).google.maps.DirectionsService();
      const r = await directions.route({
        origin: { lat: link.sourcePop.latitude!, lng: link.sourcePop.longitude! },
        destination: { lat: link.destPop.latitude!, lng: link.destPop.longitude! },
        travelMode: 'DRIVING',
        region: 'br'
      });
      const route = r.routes?.[0];
      if (!route) { setMsg('Rota não encontrada'); return; }
      const path = route.overview_path.map((p: any) => ({ lat: p.lat(), lng: p.lng() }));
      setPts(path);
      setFitTick((t) => t + 1);
      setMsg(`✓ rota sugerida (${path.length} pontos, ${(route.legs?.[0]?.distance?.value ?? 0) / 1000} km)`);
    } catch (e: any) {
      const code = e?.code ?? '';
      setMsg(code === 'REQUEST_DENIED' ? 'Directions API não ativada no GCP' : `erro: ${e?.message ?? code}`);
    } finally { setBusy(false); }
  }

  function resetStraight() {
    setPts([
      { lat: link.sourcePop.latitude!, lng: link.sourcePop.longitude! },
      { lat: link.destPop.latitude!, lng: link.destPop.longitude! }
    ]);
    setFitTick((t) => t + 1);
    setMsg(null);
  }

  const center = pts.length ? pts[Math.floor(pts.length / 2)] : SC_CENTER;
  const distKm = pathLengthKm(pts);
  const distKmSlack = distKm * 1.10;
  return (
    <Modal title={`Trajeto: ${link.sourcePop.name} ↔ ${link.destPop.name}`} onClose={onClose} wide>
      <div className="grid md:grid-cols-4 gap-3 text-xs mb-2">
        <div className="bg-slate-950 ring-1 ring-slate-800 rounded p-2">
          <div className="text-slate-400">Estado</div>
          <div className="font-semibold" style={{ color: LINK_COLOR[link.state] }}>{link.state}</div>
          <div className="text-slate-500">type: {link.type}</div>
        </div>
        <div className="bg-slate-950 ring-1 ring-slate-800 rounded p-2">
          <div className="text-slate-400">Distância</div>
          <div className="font-semibold text-sky-300">{distKm.toFixed(2)} km</div>
          <div className="text-slate-500" title="trajeto + 10% (sobras técnicas / postes)">
            +10%: <span className="text-slate-300">{distKmSlack.toFixed(2)} km</span>
          </div>
        </div>
        <div className="bg-slate-950 ring-1 ring-slate-800 rounded p-2">
          <div className="text-emerald-400 text-[10px]">▶ Ponta A</div>
          <div className="font-mono truncate text-slate-200">{link.sourceDevice.hostname}</div>
          <div className="text-slate-400 text-[10px]">iface: {link.sourceIface?.name ?? '—'}</div>
        </div>
        <div className="bg-slate-950 ring-1 ring-slate-800 rounded p-2">
          <div className="text-rose-400 text-[10px]">◀ Ponta B</div>
          <div className="font-mono truncate text-slate-200">{link.destDevice.hostname}</div>
          <div className="text-slate-400 text-[10px]">iface: {link.destIface?.name ?? '—'}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-2 text-xs">
        <button onClick={autoRoute} disabled={busy} className="px-3 py-1.5 rounded bg-sky-600 hover:bg-sky-500 disabled:opacity-50">
          {busy ? 'consultando...' : '🛣️ Sugerir via rodovias'}
        </button>
        <button onClick={resetStraight} className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600">⟂ Linha reta</button>
        <button onClick={() => setFitTick((t) => t + 1)} className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600" title="Centralizar o mapa nas pontas do enlace">↔ Encaixar</button>
        <span className="text-slate-500 self-center">{pts.length} pontos · {distKm.toFixed(2)} km</span>
        {msg && <span className={`self-center ${msg.startsWith('✓') ? 'text-emerald-400' : 'text-rose-400'}`}>{msg}</span>}
      </div>

      <div className="rounded ring-1 ring-slate-800 overflow-hidden" style={{ height: 420 }}>
        <GMap mapId={GMAPS_MAP_ID} defaultCenter={center} defaultZoom={9} gestureHandling="greedy" colorScheme="LIGHT"
          style={{ width: '100%', height: '100%' }}
          onClick={(ev: any) => { if (ev.detail?.latLng) setPts((cur) => [...cur, { lat: ev.detail.latLng.lat, lng: ev.detail.latLng.lng }]); }}>
          <FitMapToPath points={pts} runKey={`${link.id}:${fitTick}`} />
          <EditorPolyline points={pts} state={link.state} />
          {link.sourcePop.latitude != null && link.sourcePop.longitude != null && (
            <AdvancedMarker position={{ lat: link.sourcePop.latitude, lng: link.sourcePop.longitude }}>
              <PopEndpointMarker color="#10b981" arrow="▶" label={link.sourcePop.name} />
            </AdvancedMarker>
          )}
          {link.destPop.latitude != null && link.destPop.longitude != null && (
            <AdvancedMarker position={{ lat: link.destPop.latitude, lng: link.destPop.longitude }}>
              <PopEndpointMarker color="#ef4444" arrow="◀" label={link.destPop.name} />
            </AdvancedMarker>
          )}
          {pts.map((p, i) => (
            <AdvancedMarker key={i} position={p} draggable
              onDragEnd={(ev: any) => {
                if (!ev.latLng) return;
                const lat = ev.latLng.lat(), lng = ev.latLng.lng();
                setPts((cur) => cur.map((q, idx) => idx === i ? { lat, lng } : q));
              }}>
              <div style={{
                width: i === 0 || i === pts.length - 1 ? 12 : 8,
                height: i === 0 || i === pts.length - 1 ? 12 : 8,
                borderRadius: '50%',
                background: i === 0 ? '#10b981' : i === pts.length - 1 ? '#ef4444' : '#0ea5e9',
                border: '2px solid #0f172a',
                cursor: 'pointer'
              }}
              onContextMenu={(e: any) => {
                e.preventDefault();
                if (i === 0 || i === pts.length - 1) return; // não permite remover endpoints
                setPts((cur) => cur.filter((_, idx) => idx !== i));
              }}
              title={`#${i}${i === 0 ? ' (origem)' : i === pts.length - 1 ? ' (destino)' : ' — click direito remove'}`}
              />
            </AdvancedMarker>
          ))}
        </GMap>
      </div>
      <div className="text-[10px] text-slate-500 mt-1">
        Click no mapa pra adicionar waypoint · arraste o pino pra mover · click direito num pino intermediário pra remover · verde=origem, vermelho=destino
      </div>

      {link.approved === false && (
        <div className="mt-3 p-3 rounded ring-1 ring-amber-700 bg-amber-950/30">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-semibold text-amber-300">⏳ Sugestão pendente de aprovação</div>
              <div className="text-xs text-amber-200/70">Detectada via {link.source ?? '?'}. Aprove pra confirmar o enlace; ou exclua se for falso-positivo.</div>
            </div>
            <button onClick={async () => { await api.popLinkApprove(link.id); onSaved(); }}
                    className="px-3 py-1.5 text-xs bg-emerald-700 hover:bg-emerald-600 rounded text-white">✓ Aprovar</button>
          </div>
        </div>
      )}
      <LinkLivePanel linkId={link.id} />

      {/* Corrigir pontas (host + interface) — quando vinculou errado. ADMIN. */}
      {isAdmin && (
        <div className="mt-4 pt-3 border-t border-slate-800">
          {!editEnds ? (
            <button onClick={() => setEditEnds(true)} className="px-3 py-1.5 text-xs rounded ring-1 ring-slate-700 text-sky-300 hover:bg-slate-800">
              ✏ Corrigir host / interface
            </button>
          ) : (
            <>
              <h3 className="text-sm font-semibold mb-2">✏ Corrigir pontas <span className="text-slate-500 font-normal text-xs">(host deve estar no POP da ponta)</span></h3>
              <div className="grid md:grid-cols-2 gap-3 text-xs">
                <div className="space-y-1">
                  <label className="block text-emerald-400 text-[10px]">▶ Ponta A — {link.sourcePop.name}</label>
                  <select className={inp} value={srcDev} onChange={(e) => { setSrcDev(Number(e.target.value)); setSrcIf(''); }}>
                    {srcDevsQ.data?.devices.map((d) => <option key={d.id} value={d.id}>{d.hostname}</option>)}
                    {!srcDevsQ.data?.devices.some((d) => d.id === srcDev) && <option value={srcDev}>{link.sourceDevice.hostname}</option>}
                  </select>
                  <select className={inp} value={srcIf} onChange={(e) => setSrcIf(e.target.value)}>
                    <option value="">— sem iface —</option>
                    {srcIf && !(srcIfQ.data?.interfaces ?? []).some((i) => i.name === srcIf) && <option value={srcIf}>{srcIf}</option>}
                    {(srcIfQ.data?.interfaces ?? []).map((i) => <option key={i.name} value={i.name}>{i.name}{i.description ? ` · ${i.description}` : ''}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="block text-rose-400 text-[10px]">◀ Ponta B — {link.destPop.name}</label>
                  <select className={inp} value={dstDev} onChange={(e) => { setDstDev(Number(e.target.value)); setDstIf(''); }}>
                    {dstDevsQ.data?.devices.map((d) => <option key={d.id} value={d.id}>{d.hostname}</option>)}
                    {!dstDevsQ.data?.devices.some((d) => d.id === dstDev) && <option value={dstDev}>{link.destDevice.hostname}</option>}
                  </select>
                  <select className={inp} value={dstIf} onChange={(e) => setDstIf(e.target.value)}>
                    <option value="">— sem iface —</option>
                    {dstIf && !(dstIfQ.data?.interfaces ?? []).some((i) => i.name === dstIf) && <option value={dstIf}>{dstIf}</option>}
                    {(dstIfQ.data?.interfaces ?? []).map((i) => <option key={i.name} value={i.name}>{i.name}{i.description ? ` · ${i.description}` : ''}</option>)}
                  </select>
                </div>
              </div>
              {endMsg && <div className="text-rose-400 text-xs mt-1">{endMsg}</div>}
              <div className="flex gap-2 mt-2">
                <button onClick={() => { setEndMsg(null); saveEnds.mutate(); }} disabled={saveEnds.isPending}
                        className="px-3 py-1.5 text-xs rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 font-semibold">
                  {saveEnds.isPending ? 'salvando…' : 'salvar pontas'}
                </button>
                <button onClick={() => { setEditEnds(false); setEndMsg(null); setSrcDev(link.sourceDevice.id); setDstDev(link.destDevice.id); setSrcIf(link.sourceIface?.name ?? ''); setDstIf(link.destIface?.name ?? ''); }}
                        className="px-3 py-1.5 text-xs rounded text-slate-400 hover:text-slate-200">cancelar</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* F12 — Operação */}
      <div className="mt-4 pt-3 border-t border-slate-800">
        <h3 className="text-sm font-semibold mb-2">🔧 Operação</h3>
        <div className="grid md:grid-cols-4 gap-2 text-xs">
          <div>
            <label className="block text-slate-400 mb-1">Tipo do enlace</label>
            <select className={inp} value={ownership} onChange={(e) => setOwnership(e.target.value as any)}>
              <option value="OWN">Próprio (fibra SC)</option>
              <option value="SWAP">Swap (terceiro)</option>
              <option value="TRANSPORT">Transporte (operadora)</option>
            </select>
          </div>
          {ownership !== 'OWN' && (
            <>
              <div>
                <label className="block text-slate-400 mb-1">Fornecedor</label>
                <select className={inp} value={providerId ?? ''} onChange={(e) => setProviderId(e.target.value ? Number(e.target.value) : null)}>
                  <option value="">— escolha —</option>
                  {opProvidersQ.data?.providers.map((p) => (
                    <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-slate-400 mb-1">ID do circuito</label>
                <input className={inp} value={circuitId} onChange={(e) => setCircuitId(e.target.value)} placeholder="CIR-12345" />
              </div>
              <div>
                <label className="block text-slate-400 mb-1">Custo mensal (R$)</label>
                <input className={inp} type="number" step="0.01" value={costReais} onChange={(e) => setCostReais(e.target.value)} placeholder="2500.00" />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex justify-between gap-2 pt-3 mt-2 border-t border-slate-800">
        <button onClick={() => { if (confirm(`Excluir o enlace ${link.sourcePop.name} ↔ ${link.destPop.name}?`)) del.mutate(); }}
          disabled={del.isPending} className="px-3 py-1.5 rounded text-sm bg-rose-700 hover:bg-rose-600 text-white">
          🗑️ {del.isPending ? 'excluindo...' : 'excluir enlace'}
        </button>
        <div className="flex gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm text-slate-400 hover:text-slate-200">cancelar</button>
          <button onClick={() => save.mutate({ pathGeometry: null, pathSource: null })} disabled={save.isPending} className="px-3 py-1.5 rounded text-sm bg-slate-700 hover:bg-slate-600">
            limpar trajeto
          </button>
          <button onClick={() => {
              if (ownership !== 'OWN' && providerId == null) { setMsg('escolha o fornecedor'); return; }
              const body: any = { pathGeometry: pts, pathSource: msg?.startsWith('✓') ? 'directions' : 'manual', ownership };
              if (ownership === 'OWN') {
                body.providerId = null; body.providerCircuitId = null; body.monthlyCostCents = null;
              } else {
                body.providerId = providerId;
                body.providerCircuitId = circuitId.trim() || null;
                body.monthlyCostCents = costReais ? Math.round(Number(costReais) * 100) : null;
              }
              save.mutate(body);
            }} disabled={save.isPending}
            className="px-3 py-1.5 rounded text-sm bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 font-semibold">
            {save.isPending ? 'salvando...' : 'salvar trajeto'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function EditorPolyline({ points, state }: { points: Array<{lat:number;lng:number}>; state: string }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google?.maps || points.length < 2) return;
    const poly = new (window as any).google.maps.Polyline({
      path: points, strokeColor: LINK_COLOR[state] ?? '#64748b',
      strokeOpacity: 0.7, strokeWeight: 4, map
    });
    return () => poly.setMap(null);
  }, [map, JSON.stringify(points), state]);
  return null;
}

function IfaceDiscoverBtn({ deviceId, onDone }: { deviceId: number; onDone: () => void }) {
  const [msg, setMsg] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => api.ifaceDiscover(deviceId),
    onSuccess: (d) => { setMsg(`✓ ${d.total} ifaces (${d.created} novas)`); onDone(); },
    onError: (e: any) => setMsg(`erro: ${e.detail?.detail ?? e.detail?.error ?? e.message ?? 'falhou'}`)
  });
  return (
    <div className="flex items-center gap-2 mt-1">
      <button type="button" onClick={() => { setMsg(null); m.mutate(); }} disabled={m.isPending}
        className="px-2 py-1 text-[10px] rounded bg-sky-700 hover:bg-sky-600 disabled:opacity-50">
        {m.isPending ? 'descobrindo via SNMP...' : '🔍 Descobrir interfaces (SNMP)'}
      </button>
      {msg && <span className={`text-[10px] ${msg.startsWith('✓') ? 'text-emerald-400' : 'text-rose-400'}`}>{msg}</span>}
    </div>
  );
}

// ============================== POPs ==============================
function PopsPage({ canWrite }: { canWrite: boolean }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const popsQ = useQuery({ queryKey: ['pops', q], queryFn: () => api.pops(q) });
  const [editing, setEditing] = useState<Pop | null>(null);
  const [creating, setCreating] = useState(false);
  const del = useMutation({
    mutationFn: (id: number) => api.popDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pops'] }),
    onError: (e: any) => alert(deleteErrMsg(e))
  });
  const pops = popsQ.data?.pops ?? [];
  return (
    <Section title="POPs (sites)" sub={`${pops.length} pops`} action={canWrite && <PrimaryBtn onClick={() => setCreating(true)}>+ Novo POP</PrimaryBtn>}>
      <div className="mb-3 -mt-1">
        <input placeholder="buscar code/nome/cidade..." value={q} onChange={(e) => setQ(e.target.value)}
          className="bg-slate-950 ring-1 ring-slate-800 rounded px-3 py-1.5 text-xs w-full max-w-md focus:outline-none focus:ring-emerald-600" />
      </div>
      <table className="w-full text-xs">
        <thead className="text-slate-400"><tr className="border-b border-slate-800">
          <Th>UF</Th><Th>Code</Th><Th>Nome / cidade</Th><Th>Endereço</Th><Th>GPS</Th><Th>Devices</Th><Th className="w-32">Ações</Th>
        </tr></thead>
        <tbody>
          {pops.map((p) => (
            <tr key={p.id} className="border-b border-slate-800 hover:bg-slate-800/30">
              <Td>{p.uf ? <code className="text-emerald-400">{p.uf}</code> : '—'}</Td>
              <Td className="font-mono text-slate-400">{p.code}</Td>
              <Td>
                <div className="font-semibold">{p.name}</div>
                <div className="text-slate-500 text-[10px]">{p.city ?? '—'}</div>
              </Td>
              <Td className="text-slate-500 text-[10px]">{
                [p.street, p.streetNumber, p.neighborhood, p.zipCode].filter(Boolean).join(', ') || '—'
              }</Td>
              <Td className="text-slate-500 text-[10px]">{p.latitude != null ? `${p.latitude.toFixed(4)}, ${p.longitude?.toFixed(4)}` : '—'}</Td>
              <Td>{p._count?.devices ?? '—'}</Td>
              <Td>
                {canWrite && <ActionBtn onClick={() => setEditing(p)}>editar</ActionBtn>}
                {canWrite && <ActionBtn variant="danger" onClick={() => confirmDelete('POP', p.name) && del.mutate(p.id)}>x</ActionBtn>}
              </Td>
            </tr>
          ))}
          {!pops.length && <tr><td colSpan={7} className="text-center py-6 text-slate-500">sem resultados</td></tr>}
        </tbody>
      </table>
      {(editing || creating) && (
        <PopForm pop={editing ?? undefined} onClose={() => { setEditing(null); setCreating(false); }} />
      )}
    </Section>
  );
}

function PopForm({ pop, onClose }: { pop?: Pop; onClose: () => void }) {
  const qc = useQueryClient();
  const powerProvidersQ = useQuery({
    queryKey: ['providers', 'POWER'],
    queryFn: () => api.providers({ kind: 'POWER', enabled: true })
  });
  const [form, setForm] = useState({
    code: pop?.code ?? '',
    name: pop?.name ?? '',
    uf: pop?.uf ?? '',
    city: pop?.city ?? '',
    street: pop?.street ?? '',
    streetNumber: pop?.streetNumber ?? '',
    neighborhood: pop?.neighborhood ?? '',
    zipCode: pop?.zipCode ?? '',
    country: pop?.country ?? 'BR',
    latitude: pop?.latitude ?? null as number | null,
    longitude: pop?.longitude ?? null as number | null,
    notes: pop?.notes ?? '',
    enabled: pop?.enabled ?? true,
    // F12 — Energia
    powerProviderId: pop?.powerProviderId ?? null as number | null,
    powerAccountNumber: pop?.powerAccountNumber ?? '',
    hasGenerator: pop?.hasGenerator ?? false,
    hasUps: pop?.hasUps ?? false,
    powerNotes: pop?.powerNotes ?? ''
  });
  const [err, setErr] = useState<string | null>(null);
  const [geocodeMsg, setGeocodeMsg] = useState<string | null>(null);
  const [geocoding, setGeocoding] = useState(false);
  // Client-side geocode (chave restrita por referrer só funciona no browser)
  async function doGeocode() {
    const parts = [form.street, form.streetNumber, form.neighborhood, form.city, form.uf, form.zipCode, form.country].filter(Boolean);
    if (parts.length < 2) { setGeocodeMsg('preencha pelo menos cidade + UF'); return; }
    setGeocoding(true); setGeocodeMsg(null);
    try {
      if (!(window as any).google?.maps) { setGeocodeMsg('Maps JS ainda carregando...'); setGeocoding(false); return; }
      const g = new (window as any).google.maps.Geocoder();
      const { results } = await g.geocode({ address: parts.join(', '), region: 'br' });
      if (!results?.[0]) { setGeocodeMsg('endereço não encontrado'); setGeocoding(false); return; }
      const top = results[0];
      const lat = top.geometry.location.lat(); const lng = top.geometry.location.lng();
      setForm((f) => ({ ...f, latitude: lat, longitude: lng }));
      setGeocodeMsg(`✓ ${top.formatted_address}`);
    } catch (e: any) {
      setGeocodeMsg(`erro: ${e?.message ?? 'falhou'}`);
    } finally { setGeocoding(false); }
  }
  const save = useMutation({
    mutationFn: () => {
      // Validação client-side antes de bater no backend
      if (!form.code.trim()) throw new HttpError(400, 'preencha o Code (slug)', { error: 'missing_field', field: 'code' });
      if (!/^[a-z0-9-]+$/.test(form.code)) throw new HttpError(400, 'Code só aceita minúsculas, dígitos e hífens', { error: 'bad_code', field: 'code' });
      if (!form.name.trim()) throw new HttpError(400, 'preencha o Nome', { error: 'missing_field', field: 'name' });
      const body: any = { ...form };
      if (form.latitude == null || form.latitude === ('' as any)) delete body.latitude; else body.latitude = Number(form.latitude);
      if (form.longitude == null || form.longitude === ('' as any)) delete body.longitude; else body.longitude = Number(form.longitude);
      // F12 — Energia: campos vazios viram null pro backend
      body.powerProviderId = form.powerProviderId || null;
      body.powerAccountNumber = form.powerAccountNumber.trim() || null;
      body.powerNotes = form.powerNotes.trim() || null;
      if (pop) return api.popUpdate(pop.id, body);
      return api.popCreate(body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pops'] }); onClose(); },
    onError: (e: any) => {
      if (e.detail?.error === 'duplicate_code') return setErr('code já existe');
      // Erro do zod (backend) vem como { error: 'bad_request', detail: { fieldErrors: {...} } }
      const fe = e.detail?.detail?.fieldErrors as Record<string, string[]> | undefined;
      if (fe) {
        const missing = Object.entries(fe).map(([f, msgs]) => `${f}: ${msgs.join(', ')}`).join(' · ');
        return setErr(`campos inválidos — ${missing}`);
      }
      setErr(e.message ?? 'erro ao salvar');
    }
  });
  return (
    <Modal title={pop ? `Editar POP ${pop.code}` : 'Novo POP'} onClose={onClose} wide>
      <APIProvider apiKey={GMAPS_KEY}>
      <div className="grid md:grid-cols-3 gap-3">
        <FormRow label="Code (slug) *"><input className={inp} disabled={!!pop} required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase() })} placeholder="al-aguas-belas" /></FormRow>
        <FormRow label="UF"><input className={inp} maxLength={4} value={form.uf} onChange={(e) => setForm({ ...form, uf: e.target.value.toUpperCase() })} placeholder="AL / SE / PE" /></FormRow>
        <FormRow label="Habilitado"><input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} /></FormRow>
        <FormRow label="Nome (display) *" wide><input className={inp} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="AL — Águas Belas" /></FormRow>
        <FormRow label="Cidade"><input className={inp} value={form.city ?? ''} onChange={(e) => setForm({ ...form, city: e.target.value })} /></FormRow>
        <FormRow label="Rua" wide><input className={inp} value={form.street ?? ''} onChange={(e) => setForm({ ...form, street: e.target.value })} /></FormRow>
        <FormRow label="Número"><input className={inp} value={form.streetNumber ?? ''} onChange={(e) => setForm({ ...form, streetNumber: e.target.value })} /></FormRow>
        <FormRow label="Bairro"><input className={inp} value={form.neighborhood ?? ''} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} /></FormRow>
        <FormRow label="CEP"><input className={inp} value={form.zipCode ?? ''} onChange={(e) => setForm({ ...form, zipCode: e.target.value })} /></FormRow>
        <FormRow label="País"><input className={inp} value={form.country ?? 'BR'} onChange={(e) => setForm({ ...form, country: e.target.value })} /></FormRow>
        <FormRow label="Latitude"><input className={inp} type="number" step="any" value={form.latitude ?? ''} onChange={(e) => setForm({ ...form, latitude: e.target.value === '' ? null : Number(e.target.value) })} /></FormRow>
        <FormRow label="Longitude"><input className={inp} type="number" step="any" value={form.longitude ?? ''} onChange={(e) => setForm({ ...form, longitude: e.target.value === '' ? null : Number(e.target.value) })} /></FormRow>
        <FormRow label="" wide>
          <div className="flex items-center gap-2">
            <button type="button" onClick={doGeocode} disabled={geocoding}
              className="px-3 py-1.5 rounded bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-xs">
              {geocoding ? 'localizando...' : '📍 Localizar a partir do endereço'}
            </button>
            {geocodeMsg && <span className={`text-xs ${geocodeMsg.startsWith('✓') ? 'text-emerald-400' : 'text-rose-400'}`}>{geocodeMsg}</span>}
          </div>
        </FormRow>
        <FormRow label="Notas" wide><textarea className={inp + ' h-16'} value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></FormRow>
      </div>

      {GMAPS_KEY && (
        <div className="mt-3">
          <label className="block text-xs text-slate-400 mb-1">Posição no mapa <span className="text-slate-500">(arraste o pino ou clique pra ajustar)</span></label>
          <PopMiniMap
            lat={form.latitude}
            lng={form.longitude}
            onChange={(lat, lng) => setForm((f) => ({ ...f, latitude: lat, longitude: lng }))}
          />
        </div>
      )}

      {/* F12 — Energia */}
      <div className="mt-4 pt-4 border-t border-slate-800">
        <h3 className="text-sm font-semibold mb-3">⚡ Energia</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <FormRow label="Concessionária" wide>
            <select className={inp} value={form.powerProviderId ?? ''} onChange={(e) => setForm({ ...form, powerProviderId: e.target.value ? Number(e.target.value) : null })}>
              <option value="">— nenhuma —</option>
              {powerProvidersQ.data?.providers.map((p) => (
                <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
              ))}
            </select>
          </FormRow>
          <FormRow label="UC / nº cliente"><input className={inp} value={form.powerAccountNumber} onChange={(e) => setForm({ ...form, powerAccountNumber: e.target.value })} placeholder="12345-6" /></FormRow>
          <FormRow label="Gerador local"><label className="text-xs"><input type="checkbox" className="mr-2" checked={form.hasGenerator} onChange={(e) => setForm({ ...form, hasGenerator: e.target.checked })} />tem gerador</label></FormRow>
          <FormRow label="No-break"><label className="text-xs"><input type="checkbox" className="mr-2" checked={form.hasUps} onChange={(e) => setForm({ ...form, hasUps: e.target.checked })} />tem no-break/UPS</label></FormRow>
          <FormRow label="Notas de energia" wide><textarea className={inp + ' h-16'} value={form.powerNotes} onChange={(e) => setForm({ ...form, powerNotes: e.target.value })} placeholder="trafo, padrão, autonomia, etc" /></FormRow>
        </div>
        <div className="text-[11px] text-slate-500 mt-1">
          Cadastre concessionárias em <a href="#providers" className="text-emerald-400 hover:underline">Operação → Fornecedores</a> (tipo Energia).
        </div>
      </div>

      {err && <div className="text-rose-400 text-sm">{err}</div>}
      <FormActions onClose={onClose} onSave={() => { setErr(null); save.mutate(); }} pending={save.isPending} />
      </APIProvider>
    </Modal>
  );
}

function PopMiniMap({ lat, lng, onChange }: { lat: number | null; lng: number | null; onChange: (lat: number, lng: number) => void }) {
  const center = (lat != null && lng != null) ? { lat, lng } : SC_CENTER;
  const hasPin = lat != null && lng != null;
  return (
    <>
      <div className="rounded ring-1 ring-slate-800 overflow-hidden" style={{ height: 280 }}>
        <GMap           mapId={GMAPS_MAP_ID}
          defaultCenter={center}
          defaultZoom={hasPin ? 14 : 7}
          gestureHandling="greedy"
          colorScheme="LIGHT"
          style={{ width: '100%', height: '100%' }}
          onClick={(ev: any) => { if (ev.detail?.latLng) onChange(ev.detail.latLng.lat, ev.detail.latLng.lng); }}
        >
          {hasPin && (
            <AdvancedMarker
              position={center}
              draggable
              onDragEnd={(ev: any) => { if (ev.latLng) onChange(ev.latLng.lat(), ev.latLng.lng()); }}
            >
              <Pin background="#10b981" borderColor="#0f172a" glyphColor="#0f172a" />
            </AdvancedMarker>
          )}
        </GMap>
      </div>
      {!hasPin && <div className="text-xs text-slate-500 mt-1">Clique no mapa pra colocar o pino, ou use "Localizar a partir do endereço" / digite lat-lng manualmente.</div>}
    </>
  );
}

// ============================== BGP ==============================
const STATE_COLORS: Record<string, string> = {
  ESTABLISHED: 'text-emerald-400 bg-emerald-500/10 ring-emerald-700/40',
  ACTIVE: 'text-amber-400 bg-amber-500/10 ring-amber-700/40',
  CONNECT: 'text-amber-400 bg-amber-500/10 ring-amber-700/40',
  IDLE: 'text-rose-400 bg-rose-500/10 ring-rose-700/40',
  OPEN_SENT: 'text-amber-400 bg-amber-500/10 ring-amber-700/40',
  OPEN_CONFIRM: 'text-amber-400 bg-amber-500/10 ring-amber-700/40',
  ADMIN_DOWN: 'text-slate-400 bg-slate-500/10 ring-slate-700/40',
  UNKNOWN: 'text-slate-400 bg-slate-500/10 ring-slate-700/40'
};
function StateBadge({ state }: { state: string }) {
  return <span className={`px-1.5 py-0.5 rounded text-[10px] ring-1 ${STATE_COLORS[state] ?? STATE_COLORS.UNKNOWN}`}>{state}</span>;
}
function fmtUptime(s: number | null): string {
  if (s == null) return '—';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}m`;
  return `${Math.floor(s / 86400)}d${String(Math.floor((s % 86400) / 3600)).padStart(2, '0')}h`;
}

function BgpOverviewPage() {
  const ovQ = useQuery({ queryKey: ['bgp-overview'], queryFn: api.bgpOverview, refetchInterval: 30_000 });
  const evQ = useQuery({ queryKey: ['bgp-events'], queryFn: () => api.bgpEvents(50), refetchInterval: 30_000 });
  const meQ = useQuery({ queryKey: ['me'], queryFn: api.me, retry: false, staleTime: 60_000 });
  const canBulk = meQ.data?.user.role === 'ADMIN' || !!meQ.data?.permissions?.includes('BGP_PEER_ADMIN');
  if (!ovQ.data) return <Splash msg="carregando..." />;
  const o = ovQ.data;
  return (
    <section className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold">BGP — visão geral</h1>
        <div className="flex-1" />
        {canBulk && (
          <button onClick={() => { window.location.hash = '#bgp/massa'; }}
            className="px-3 py-1 rounded text-xs ring-1 ring-slate-700 text-slate-300 hover:bg-slate-800">Desativar/reativar em massa</button>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card label="Total peers" value={o.total} />
        <Card label="Established" value={o.byState['ESTABLISHED'] ?? 0} accent="emerald" />
        <Card label="Active/Connect" value={(o.byState['ACTIVE'] ?? 0) + (o.byState['CONNECT'] ?? 0)} />
        <Card label="Idle" value={o.byState['IDLE'] ?? 0} />
        <Card label="Desativados (ignore)" value={o.byState['ADMIN_DOWN'] ?? 0} />
        <Card label="Devices BGP" value={o.devices.length} />
      </div>
      {!!o.removed && (
        <div className="text-[11px] text-slate-500">
          {o.removed} peer{o.removed === 1 ? '' : 's'} removido{o.removed === 1 ? '' : 's'} da configuração dos equipamentos ficam fora das contas (aparecem riscados no detalhe do roteador).
        </div>
      )}

      <Section title="Devices BGP-active" sub={`${o.devices.length} roteadores`}>
        <table className="w-full text-xs">
          <thead className="text-slate-400"><tr className="border-b border-slate-800">
            <Th>Hostname</Th><Th>Vendor</Th><Th>Location</Th><Th>ASN</Th><Th>Total</Th><Th>Established</Th><Th>Last change</Th><Th />
          </tr></thead>
          <tbody>
            {o.devices.map((d) => {
              const fail = d.total - d.established;
              return (
                <tr key={d.id} className="border-b border-slate-800 hover:bg-slate-800/30 cursor-pointer" onClick={() => { window.location.hash = `#bgp/${d.id}`; }}>
                  <Td className="font-mono text-slate-200">{d.hostname}</Td>
                  <Td><code className="text-emerald-400">{d.vendor?.code}</code></Td>
                  <Td className="text-slate-400">{d.location ?? '—'}</Td>
                  <Td>{d.asn ?? '—'}</Td>
                  <Td>{d.total}</Td>
                  <Td className={fail > 0 ? 'text-amber-400' : 'text-emerald-400'}>{d.established}{fail > 0 && ` (${fail} fora)`}</Td>
                  <Td className="text-slate-500 text-[10px]">{d.lastChange ? new Date(d.lastChange).toLocaleString() : '—'}</Td>
                  <Td className="text-slate-500">›</Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Section>

      <Section title="Eventos recentes" sub={`${evQ.data?.events.length ?? 0} eventos`}>
        {evQ.data?.events.length === 0 ? <div className="text-xs text-slate-500">Sem eventos.</div> : (
          <table className="w-full text-xs">
            <thead className="text-slate-400"><tr className="border-b border-slate-800">
              <Th>Quando</Th><Th>Severity</Th><Th>Device</Th><Th>Mensagem</Th>
            </tr></thead>
            <tbody>
              {evQ.data?.events.map((e) => (
                <tr key={e.id} className="border-b border-slate-800">
                  <Td className="text-slate-500 text-[10px] whitespace-nowrap">{new Date(e.ts).toLocaleString()}</Td>
                  <Td><span className={`px-1.5 py-0.5 rounded text-[10px] ring-1 ${
                    e.severity === 'info' ? 'text-emerald-400 ring-emerald-700/40' :
                    e.severity === 'warning' ? 'text-amber-400 ring-amber-700/40' :
                    'text-rose-400 ring-rose-700/40'
                  }`}>{e.severity}</span></Td>
                  <Td><button className="text-emerald-400 hover:underline" onClick={() => { window.location.hash = `#bgp/${e.device.id}`; }}>{e.device.hostname}</button></Td>
                  <Td className="text-slate-300">{e.message}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </section>
  );
}

function BgpDeviceDetail({ deviceId }: { deviceId: number }) {
  const devQ = useQuery({ queryKey: ['device', deviceId], queryFn: () => api.device(deviceId) });
  const peersQ = useQuery({ queryKey: ['bgp-peers', deviceId], queryFn: () => api.bgpPeers(deviceId), refetchInterval: 15_000 });
  if (!devQ.data || !peersQ.data) return <Splash msg="carregando..." />;
  const d = devQ.data.device;
  const allPeers = peersQ.data.peers;
  const peers = allPeers.filter((p) => !p.removed);
  const removed = allPeers.filter((p) => p.removed);
  const stateBreakdown: Record<string, number> = {};
  peers.forEach((p) => { stateBreakdown[p.state] = (stateBreakdown[p.state] ?? 0) + 1; });
  const totalPfx = peers.reduce((acc, p) => acc + (p.receivedPfx ?? 0), 0);

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => { window.location.hash = '#bgp'; }} className="text-xs text-slate-400 hover:text-emerald-400">← voltar</button>
        <h1 className="text-xl font-semibold font-mono">{d.hostname}</h1>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <Card label="Total peers" value={peers.length} />
        <Card label="Established" value={stateBreakdown['ESTABLISHED'] ?? 0} accent="emerald" />
        <Card label="Não-ESTAB" value={peers.length - (stateBreakdown['ESTABLISHED'] ?? 0)} />
        <Card label="Prefixes (total)" value={totalPfx} />
      </div>
      <Section title="Peers" sub={`${peers.length} sessões${removed.length ? ` · ${removed.length} removidas do equipamento` : ''}`}>
        <table className="w-full text-xs">
          <thead className="text-slate-400"><tr className="border-b border-slate-800">
            <Th>Peer IP</Th><Th>AS</Th><Th>Descrição</Th><Th>State</Th><Th>Family</Th><Th>Uptime</Th><Th>Received</Th><Th>Last change</Th>
          </tr></thead>
          <tbody>
            {allPeers.map((p) => (
              <tr key={p.id} className={`border-b border-slate-800 ${p.removed ? 'opacity-50' : ''}`}
                title={p.removed ? `Não aparece mais no equipamento desde ${p.updatedAt ? new Date(p.updatedAt).toLocaleString() : '—'} (último estado lido)` : undefined}>
                <Td className={`font-mono text-slate-200 ${p.removed ? 'line-through' : ''}`}>{p.peerIp}</Td>
                <Td>{p.peerAsn ?? '—'}</Td>
                <Td className="text-slate-300">{p.description ?? '—'}</Td>
                <Td>{p.removed
                  ? <span className="px-1.5 py-0.5 rounded text-[10px] ring-1 text-slate-400 ring-slate-600">removido</span>
                  : <StateBadge state={p.state} />}</Td>
                <Td className="text-slate-400 text-[10px]">{p.family.replace('_', ' ')}</Td>
                <Td className="text-slate-400">{fmtUptime(p.uptimeSec)}</Td>
                <Td>{p.receivedPfx ?? '—'}</Td>
                <Td className="text-slate-500 text-[10px]">{p.lastChange ? new Date(p.lastChange).toLocaleString() : '—'}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </section>
  );
}

// ============================== UI primitives ==============================
const inp = "w-full bg-slate-950 ring-1 ring-slate-800 rounded px-3 py-2 text-sm focus:outline-none focus:ring-emerald-600";

function Section({ title, sub, action, children }: { title: string; sub?: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="bg-slate-900 rounded ring-1 ring-slate-800">
      <header className="px-4 py-3 border-b border-slate-800 flex items-center gap-3">
        <h1 className="font-semibold">{title}</h1>
        {sub && <span className="text-xs text-slate-400">{sub}</span>}
        <div className="ml-auto">{action}</div>
      </header>
      <div className="p-3 overflow-x-auto">{children}</div>
    </section>
  );
}
function Th({ children, className = '' }: any) { return <th className={`text-left px-3 py-2 font-medium ${className}`}>{children}</th>; }
function Td({ children, className = '' }: any) { return <td className={`px-3 py-1.5 align-top ${className}`}>{children}</td>; }
function PrimaryBtn({ children, onClick }: any) {
  return <button onClick={onClick} className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-xs font-semibold">{children}</button>;
}
function ActionBtn({ children, onClick, variant = 'default' }: any) {
  const cls = variant === 'danger'
    ? 'text-rose-400 hover:bg-rose-500/10'
    : 'text-slate-300 hover:bg-slate-800';
  return <button onClick={onClick} className={`mr-1 px-2 py-0.5 rounded text-xs ring-1 ring-slate-700 ${cls}`}>{children}</button>;
}
function Modal({ title, children, onClose, wide }: { title: string; children: React.ReactNode; onClose: () => void; wide?: boolean }) {
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className={`bg-slate-900 ring-1 ring-slate-800 rounded shadow-2xl w-full ${wide ? 'max-w-3xl' : 'max-w-md'} max-h-[90vh] overflow-y-auto`}>
        <div className="px-4 py-3 border-b border-slate-800 flex items-center">
          <h2 className="font-semibold">{title}</h2>
          <button onClick={onClose} className="ml-auto text-slate-500 hover:text-slate-300 text-lg">×</button>
        </div>
        <div className="p-4 space-y-3">{children}</div>
      </div>
    </div>
  );
}
function FormRow({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? 'md:col-span-2' : ''}>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

/**
 * Combobox: input de busca com dropdown de opções filtradas em tempo real.
 * Usado quando listas ficam grandes (POPs 30+, modelos 200+). Substitui `<select>`.
 *
 * - Click no input ou foco abre dropdown
 * - Digitar filtra (case-insensitive, busca em `label` + opcional `aliases`)
 * - Limita 50 visíveis (perf) — busca cobre o universo todo
 * - Click numa opção fecha + seleciona; click fora fecha
 * - Botão "limpar" se já tem valor selecionado
 */
function Combobox<T extends { id: number; label: string; aliases?: string }>({
  options, value, onChange, placeholder, autoFocus
}: {
  options: T[];
  value: number | null | undefined;
  onChange: (id: number | null) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.id === value);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options.slice(0, 50);
    return options.filter((o) => o.label.toLowerCase().includes(q) || (o.aliases?.toLowerCase().includes(q) ?? false)).slice(0, 50);
  }, [options, search]);

  return (
    <div className="relative">
      <input
        className={inp}
        placeholder={placeholder ?? 'buscar…'}
        value={open ? search : (selected?.label ?? '')}
        autoFocus={autoFocus}
        onFocus={() => { setSearch(''); setOpen(true); }}
        // Click no input já focado também reabre o dropdown — sem isso, depois
        // de selecionar, o usuário precisava blur/focus pra apagar e trocar.
        onClick={() => { if (!open) { setSearch(''); setOpen(true); } }}
        // delay no blur pra permitir click numa opção (mousedown do botão dispara antes)
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        // Qualquer digitação reabre — cobre o caso de o input ter foco mas
        // open=false (logo após selecionar). Backspace funciona pra editar/limpar.
        onChange={(e) => { setSearch(e.target.value); if (!open) setOpen(true); }}
        onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
      />
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-64 overflow-auto bg-slate-900 ring-1 ring-slate-700 rounded shadow-lg text-sm">
          {value != null && (
            <button onMouseDown={(e) => { e.preventDefault(); onChange(null); setOpen(false); }}
                    className="block w-full text-left px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-800 italic border-b border-slate-800">
              — limpar seleção —
            </button>
          )}
          {filtered.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">nada encontrado</div>}
          {filtered.map((o) => (
            <button key={o.id} type="button"
                    onMouseDown={(e) => { e.preventDefault(); onChange(o.id); setOpen(false); }}
                    className={`block w-full text-left px-3 py-1.5 hover:bg-slate-800 ${o.id === value ? 'text-emerald-400' : 'text-slate-200'}`}>
              {o.label}
            </button>
          ))}
          {options.length > filtered.length && (
            <div className="px-3 py-1 text-[10px] text-slate-600 border-t border-slate-800">
              {filtered.length} de {options.length} — refine a busca pra ver mais
            </div>
          )}
        </div>
      )}
    </div>
  );
}
function FormActions({ onClose, onSave, pending }: { onClose: () => void; onSave: () => void; pending: boolean }) {
  return (
    <div className="flex justify-end gap-2 pt-3 mt-2 border-t border-slate-800">
      <button onClick={onClose} className="px-3 py-1.5 rounded text-sm text-slate-400 hover:text-slate-200">cancelar</button>
      <button onClick={onSave} disabled={pending} className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-sm font-semibold">{pending ? 'salvando...' : 'salvar'}</button>
    </div>
  );
}
function parseRoute(): Route {
  // F11 — redirect legacy `#firmware/device/<id>` → `#device/<id>/firmware`
  const hashRaw = window.location.hash.replace(/^#/, '');
  const legacyFw = hashRaw.match(/^firmware\/device\/(\d+)(\/deploy\/(\d+))?$/);
  if (legacyFw) {
    const newHash = legacyFw[3]
      ? `#device/${legacyFw[1]}/firmware/deploy/${legacyFw[3]}`
      : `#device/${legacyFw[1]}/firmware`;
    window.location.replace(newHash);
  }
  // F12 — redirect legacy `#vendors` → `#catalog`
  if (hashRaw === 'vendors' || hashRaw.startsWith('vendors/')) {
    const newHash = '#catalog' + (hashRaw.length > 'vendors'.length ? hashRaw.slice('vendors'.length) : '');
    window.location.replace(newHash);
  }
  const h = hashRaw.split('/')[0];
  return (['dashboard', 'vendors', 'models', 'catalog', 'devices', 'device', 'cto', 'pops', 'bgp', 'optical', 'map', 'lldp', 'mpls', 'events', 'health', 'olts', 'onus', 'onu-signal', 'onu-flapping', 'onu-sanitation', 'backups', 'firmware', 'providers', 'users', 'groups', 'workers', 'agente-noc', 'agente-atendimento', 'topo3d'].includes(h) ? h : 'dashboard') as Route;
}
function parseSubRoute(): string | null {
  const parts = window.location.hash.replace(/^#/, '').split('/');
  // Retorna tudo após o primeiro segmento (suporta multi-segment ex.: "device/123" do firmware).
  return parts.length > 1 ? parts.slice(1).join('/') : null;
}
function confirmDelete(type: string, name: string) {
  return window.confirm(`Tem certeza que quer deletar este ${type}?\n\n${name}`);
}
function deleteErrMsg(e: any): string {
  if (e?.status === 409) {
    if (e.detail?.error === 'has_dependencies') {
      const c = e.detail.detail ?? {};
      return `Não pode deletar: existem dependências (${Object.entries(c).map(([k, v]) => `${k}=${v}`).join(', ')}).`;
    }
    if (e.detail?.error === 'duplicate_code') return 'code duplicado';
  }
  return e?.message ?? 'erro';
}

// Central de Saúde — badge global de pendências (visível a qualquer autenticado)
function HealthBadge({ data }: { data?: HealthOverview }) {
  const [open, setOpen] = useState(false);
  if (!data || data.rollup.total === 0) return null;
  const problems = data.categories.filter((c) => c.severity !== 'ok');
  const hasCrit = data.rollup.critical > 0;
  const btnCls = hasCrit
    ? 'bg-rose-900/40 text-rose-200 ring-rose-700/50 hover:bg-rose-900/60'
    : 'bg-amber-900/40 text-amber-200 ring-amber-700/50 hover:bg-amber-900/60';
  const dot = (sev: string) => sev === 'critical' ? 'bg-rose-400' : sev === 'warning' ? 'bg-amber-400' : 'bg-slate-500';
  return (
    <div className="relative" onMouseLeave={() => setOpen(false)}>
      <button onClick={() => setOpen((v) => !v)} onMouseEnter={() => setOpen(true)}
              className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded ring-1 ${btnCls}`}
              title="Central de Saúde">
        <span>⚠</span>
        <span className="font-mono">{data.rollup.total}</span>
        <span>{data.rollup.total > 1 ? 'alertas' : 'alerta'}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-slate-900 ring-1 ring-slate-700 rounded shadow-lg w-80 z-30">
          <div className="px-3 py-2 border-b border-slate-800 flex items-center justify-between">
            <span className="text-xs text-slate-400">Central de Saúde</span>
            <a href="#health" onClick={() => setOpen(false)} className="text-[10px] text-sky-400 hover:underline">ver tudo →</a>
          </div>
          <div className="divide-y divide-slate-800">
            {problems.map((c) => (
              <a key={c.key} href={c.route} onClick={() => setOpen(false)}
                 className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-slate-800/50">
                <span className={`w-2 h-2 rounded-full shrink-0 ${dot(c.severity)}`} />
                <span className="text-slate-200">{c.label}</span>
                <span className="ml-auto font-mono text-slate-400">{c.count > 0 ? c.count : '⚠'}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// F10.12 — badge no header pra deploys ativos (visível pra qualquer operador)
function FirmwareDeployBadge({ items }: { items: any[] }) {
  const [open, setOpen] = useState(false);
  if (!items || items.length === 0) return null;
  const transferring = items.filter((i) => i.status === 'TRANSFERRING' && i.progressPct != null);
  return (
    <div className="relative" onMouseLeave={() => setOpen(false)}>
      <button onClick={() => setOpen((v) => !v)} onMouseEnter={() => setOpen(true)}
              className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-amber-900/40 text-amber-200 ring-1 ring-amber-700/50 hover:bg-amber-900/60">
        <span>⚡</span>
        <span className="font-mono">{items.length}</span>
        <span>{items.length > 1 ? 'ações' : 'ação'}</span>
        {transferring[0]?.progressPct != null && (
          <span className="font-mono text-amber-300">· {transferring[0].progressPct}%</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-slate-900 ring-1 ring-slate-700 rounded shadow-lg w-96 max-h-96 overflow-auto z-30">
          <div className="px-3 py-2 border-b border-slate-800 text-xs text-slate-400">Ações de firmware em andamento</div>
          <div className="divide-y divide-slate-800">
            {items.map((it: any) => {
              const sizeMb = it.progressTotal ? (Number(it.progressTotal) / (1024 * 1024)).toFixed(0) : null;
              const doneMb = it.progressBytes ? (Number(it.progressBytes) / (1024 * 1024)).toFixed(0) : null;
              return (
                <a key={it.id}
                   href={`#device/${it.device.id}/firmware/deploy/${it.id}`}
                   onClick={() => setOpen(false)}
                   className="block px-3 py-2 text-xs hover:bg-slate-800/50">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-slate-200 truncate">{it.device.hostname}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ring-1 ${
                      it.status === 'TRANSFERRING' ? 'bg-sky-900/40 text-sky-300 ring-sky-700/40' :
                      'bg-slate-800 text-slate-300 ring-slate-700/40'
                    }`}>{it.operation} · {it.status}</span>
                  </div>
                  <div className="text-slate-500 text-[10px]">
                    {it.firmware?.version ?? '—'}
                    {it.startedBy && <> · por {it.startedBy.email}</>}
                  </div>
                  {it.progressPct != null && it.status === 'TRANSFERRING' && (
                    <div className="mt-1">
                      <div className="h-1.5 bg-slate-800 rounded overflow-hidden">
                        <div className="h-full bg-sky-500 transition-all" style={{ width: `${it.progressPct}%` }} />
                      </div>
                      <div className="text-[9px] text-slate-500 font-mono mt-0.5">
                        {doneMb}/{sizeMb} MB · {it.progressPct}% · {it.progressFile}
                      </div>
                    </div>
                  )}
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
